/**
 * ProductionGridService — SharePoint persistence for the Production Grid
 * (Manufacturing → BQC1 Planning, BQC2 Cutting, BQC3 Dispatch, BQC4
 * Receiving, BQC5 QC).
 *
 * Backed by three lists on the Manufacturing site (see mfgSite.ts) —
 * provisioned by GridProvisioningService with exactly this schema, which
 * mirrors the legacy i-SYNERGYTest lists (confirmed via REST field dump,
 * 2026-07-07):
 *   iSyn_Batches      — one row per batch (Title/wkSeries = "WK 0495-0401")
 *   iSyn_WIPs         — one row per WIP, BatchID lookup -> iSyn_Batches
 *   iSyn_StageHistory — append-only per-WIP stage transition log
 *
 * HISTORY / WARNING: an earlier version of this service guessed at
 * iSyn_WIPs field names and self-provisioned any that appeared missing, on
 * every page load. Several guessed names collided case-insensitively with
 * real existing fields (OrderId vs orderId, Sku vs sku, …) — SharePoint
 * silently renamed the new one instead of erroring, so it kept multiplying
 * junk columns on the live production list undetected. Schema
 * self-provisioning is permanently disabled for these lists — never add a
 * createfieldasxml path back here. Map to the confirmed fields below only.
 *
 * iSyn_Batches schema (InternalName -> TS field):
 *   Title/wkSeries -> batchCode      (Text, required — e.g. "WK 0493-0320")
 *   dateOrdered    -> dateOrdered    (DateTime, required)
 *   prodPlan       -> prodPlan       (Number, required)
 *   cutTotal/assignedTotal/receivedTotal/qcTotal -> cutTotal/assignedTotal/receivedTotal/qcTotal (Number)
 *   batchStatus    -> batchStatus    (Choice, required: Active|Completed|Stale|Cancelled)
 *   notes          -> notes          (Note)
 *
 * iSyn_WIPs schema (InternalName -> TS field):
 *   wipNumber      -> wipId          (Text, required — e.g. "26031292")
 *   orderId/serialId/cutter/design/sku -> same names (Text; design/sku required)
 *   category       -> category       (Choice, required: CLASSIC|ELITE|ALTA|PRIMO|ELITE PRO)
 *   qty            -> qty            (Number, required)
 *   currentStage   -> currentStage   (Choice, required: BQC1|BQC2|BQC3|BQC4|BQC5|DONE|LOST|DAMAGED)
 *   dateCreated    -> dateCreated    (DateTime, required)
 *   dueDate        -> dueDate        (DateTime)
 *   storeChannel   -> storeChannel   (Choice: LAZ IG|SHP IG|TIKTOK|IGCOM|OTHER)
 *   notes          -> notes          (Note)
 *   BatchID        -> batchId/batchCode (Lookup -> iSyn_Batches; write via BatchIDId)
 *   AssignedSewer  -> assignedSewer  (Text)
 *   color/size/assignedDS/deliveryStatus -> same names (Text)
 *   receivedQty    -> receivedQty    (Number)
 *   dispatchedAt/receivedAt -> same names (DateTime)
 *   cutOperator    -> cutOperator    (Text)
 *
 * iSyn_StageHistory schema:
 *   wipId          -> wipId          (Text, required)
 *   fromStage      -> fromStage      (Choice: BQC1-BQC5)
 *   toStage        -> toStage        (Choice, required: BQC1-BQC5|DONE)
 *   transitionAt   -> transitionAt   (DateTime, required)
 *   operator       -> operator       (Text, required)
 */

import { createSharePointClient } from "./spClient";
import { batchCodeIndex } from "./batchCodes";
import { rowsToRetry, refusedStatuses, systemicRefusal, firstBatchError } from "./batchStatuses";
import { getSharePointToken } from "../../shell/auth/tokens";
import { MFG_SITE_URL } from "./mfgSite";
import { gridProvisioningService } from "./GridProvisioningService";
import { comparableFields, type IDedupeRow } from "../../modules/mfg-workcenters/wipDedupe";
import { retentionCutoff, withinRetention, staleRows } from "../../modules/production-grid/stageHistoryRetention";
import {
  planOnlyOmitFields, fullImportOmitFields, planBatchOmitFields,
} from "../../modules/production-grid/joPlanImport";
import { idRanges, mapPool } from "./wipIdRanges";
import { categoryOfParent } from "../../modules/production-grid/parentCategories";
import { parentSkuOf } from "../../modules/production-grid/costing";
import { CATEGORIES } from "./categories";

const WIPS_LIST = "iSyn_WIPs";
/** The categories the SharePoint Choice column accepts — see previewCategoryRetag. */
const CATEGORY_SET: ReadonlySet<string> = new Set<string>(CATEGORIES);

/** One WIP whose stored category disagrees with the product catalog. */
export interface ICategoryRetagRow {
  spId: number;
  wipId: string;
  from?: string;
  to: Category;
}

export interface ICategoryRetagPlan {
  /** Rows that would be written — one column each. */
  fix: ICategoryRetagRow[];
  /** Stored value already equals the catalog's. */
  alreadyRight: number;
  /** No SKU, or a parent the catalog does not list — left exactly as it is. */
  unknownDesign: number;
  /** Catalog says LAB GOWN, which the Choice column has no value for. */
  notAnAppCategory: number;
  /** WIPs read. */
  scanned: number;
}
const BATCHES_LIST = "iSyn_Batches";
const STAGE_HISTORY_LIST = "iSyn_StageHistory";
/**
 * How much stage history is kept.
 *
 * THE TABLE IS DERIVED AND WAS UNBOUNDED, which is the actual defect behind
 * "why can't this list accept writes any more". Every row is rebuilt from the
 * workbook by any import; nothing is authored here. Left to grow it passed
 * SharePoint's 5,000-item view threshold, at which point an unindexed filter
 * returns a bare 500 — first on reads, and by 2026-08-31 on the creates too.
 *
 * Capping it is what stops that recurring. 120 days matches the day snapshots'
 * own retention, and covers every window the two consumers ask for (Batch
 * Detail's time-in-stage and Control Tower's history). The KPI tiles do not
 * read this table at all — the daily-output aggregates are their source — so
 * the cap costs nothing there.
 *
 * Anything older is pruned on import rather than at read time: a row nobody
 * can reach is still a row SharePoint counts toward the threshold.
 */
export const STAGE_HISTORY_RETENTION_DAYS = 120;
/** Rows pruned per import. Bounded so the first run after this shipped — which
 *  has years of backlog to clear — cannot turn one import into an hour of
 *  deletes; the next import simply continues where it left off. */
const STAGE_HISTORY_PRUNE_PER_RUN = 2000;

/** The product lines. Defined in categories.ts — a pure, import-free module,
 * so the parser and the costing rollup can use it without dragging MSAL into
 * their tests. Re-exported here because this is where callers look for it. */
export { CATEGORIES, isCategory } from "./categories";
export type { Category } from "./categories";
import type { Category } from "./categories";
export type CurrentStage = "BQC1" | "BQC2" | "BQC3" | "BQC4" | "BQC5" | "DONE" | "LOST" | "DAMAGED";
export type StoreChannel = "LAZ IG" | "SHP IG" | "TIKTOK" | "IGCOM" | "OTHER";
export type BatchStatus = "Active" | "Completed" | "Stale" | "Cancelled";
export type StageChoice = "BQC1" | "BQC2" | "BQC3" | "BQC4" | "BQC5";
/** BQC1's CANCELLATION STATUS column, classified — mirrors
 * parseProductionGridWorkbook.ts's own CancellationState. Kept as a separate,
 * structurally-identical type rather than imported from there: this module
 * (src/lib/sp) sits BELOW the production-grid feature module in the app's
 * layering, and importing from it here would invert that. */
export type WipCancellationStatus = "none" | "requested" | "cancelled";

export interface IProductionWip {
  spId: number;
  wipId: string;
  orderId?: string;
  serialId?: string;
  cutter?: string;
  design?: string;
  sku: string;
  category?: Category;
  qty: number;
  currentStage?: CurrentStage;
  dateCreated?: string;
  dueDate?: string;
  storeChannel?: StoreChannel;
  notes?: string;
  batchId?: number;
  batchCode?: string;
  assignedSewer?: string;
  color?: string;
  size?: string;
  assignedDS?: string;
  deliveryStatus?: string;
  receivedQty?: number;
  dispatchedAt?: string;
  receivedAt?: string;
  /** When BQC2's own sheet says this bundle was cut. The per-WIP proof that
   *  the import already counted its cut — the cut-side twin of receivedAt and
   *  qcPassedAt. See liveStageTopUp. */
  cutAt?: string;
  cutOperator?: string;
  modified?: string;
  /** BQC1's own JO SERIES column — raw, not otherwise derived. */
  joSeries?: string;
  /** BQC1's own WK Series column, raw (e.g. "WK05090708") — distinct from
   * batchCode, which is the same value reformatted for display/grouping
   * ("WK 0509-0708"). Kept alongside it so the literal source value is never
   * lost to formatting. */
  wkSeries?: string;
  /** BQC1's DATE ASSIGNED TO CUTTER column, literally — the date a WIP was
   * handed to a cutter, as BQC1 itself recorded it. Distinct from
   * dateCreated, which falls back to other sources when this is blank. */
  dateAssignedToCutter?: string;
  /** BQC1's "WIP BQC6 (INBOUNDED TO RTG STOCKROOM)" marker — true only when
   * that column names this exact WIP, never on a lookup miss ("NO WIP") or a
   * stray non-blank value. */
  inboundedToRtgStockroom?: boolean;
  /** BQC1's PACKAGING marker — true when the order is already packed. */
  packed?: boolean;
  /** BQC1's CANCELLATION STATUS column, classified into one of three outcomes
   * (see WipCancellationStatus) — the wording varies ("Cancelled" /
   * "Canceled" / "Cancellation requested") but the classification doesn't. */
  cancellationStatus?: WipCancellationStatus;
  /**
   * What the MARKETPLACE says became of this bundle's order, joined on
   * ORDER ID after the read — see orderStatusJoin.
   *
   * DERIVED, NEVER A COLUMN, and the twin of the pair on ISlimWip: iSyn_WIPs
   * holds neither and must not, because a bulk write of somebody else's
   * export may never reach the mother table. Nothing may name them in a
   * `$select`.
   */
  orderStatus?: "CANCELLED" | "COMPLETED";
  orderLabel?: "CANCELLED" | "SHIPPED" | "COMPLETED";
  orderCancelled?: boolean;
  /** BQC1's DIRECT MATL column — per-WIP direct material cost. */
  directMatl?: number;
  /** BQC1's COGM column — per-WIP cost of goods manufactured. */
  cogm?: number;
  /** BQC4's own GRN column — the clerk's real, hand-assigned Goods Receipt
   * Number, read via an XLOOKUP against RECEIVING USERFORM's own RECEIVING
   * sheet (2026-08-18 design conversation with Mikko). The same number
   * ISlip.grn holds for an app-issued slip; for a historical slip (never
   * scanned in this app) this is the ONLY source — see receivingLedger.ts's
   * historicalReceiveTally. */
  grn?: string;
  /** BQC5's own reconciled passed-sets figure — the same capped/reconciled
   * value this parser already computes for the batch-level qcTotal rollup,
   * exposed per WIP too (2026-08-19 design conversation with Mikko,
   * mirroring receivedQty/grn above for QC's side of the fix). For a
   * historical WIP (never scanned by this app's own QC station) this is
   * the ONLY source — see qcLedger.ts's historicalQcTally. */
  qcPassedQty?: number;
  /** BQC5's own QC date, same cascade the parser already uses to pick
   * qcDate — cut/BQC6 fallbacks included, so this is "when this WIP's
   * current QC state was last established", not strictly a BQC5 row's own
   * stamp. */
  qcPassedAt?: string;
  /**
   * BQC5's QC PERSONNEL column — who the sheet names as the inspector on this
   * WIP's latest BQC5 row.
   *
   * The QC-side twin of grn: for a bundle inspected before this station
   * existed there is no iSyn2_QcScans row to read a name off, and the
   * workbook's own column is the ONLY record of who looked at it. Aggregated
   * the way every other stage extra is — last non-empty value across the
   * WIP's rows.
   */
  qcPersonnel?: string;
  /**
   * Sets sent back for rework off the "BQC5 REPAIR" sheet, summed across every
   * repair row for this WIP.
   *
   * DELIBERATELY NOT CAPPED at the WIP's declared qty, unlike qcPassedQty. This
   * is an EVENT tally, not a state: a two-set bundle that fails twice really
   * did have four sets handled by the repair loop, and clamping it to two would
   * hide the second round. For the same reason it is never fed into IQcTally's
   * `repair`, which means "outstanding right now" — see qcLedger.ts's
   * historicalQcTally, which keeps that field at 0 on purpose.
   */
  qcRepairQty?: number;
}

export interface IBatchHeader {
  spId?: number;
  batchCode: string;
  dateOrdered?: string;
  prodPlan: number;
  cutTotal?: number;
  assignedTotal?: number;
  receivedTotal?: number;
  qcTotal?: number;
  batchStatus: BatchStatus;
  notes?: string;
}

export interface IStageHistoryEntry {
  spId?: number;
  wipId: string;
  fromStage?: StageChoice;
  toStage: StageChoice | "DONE";
  transitionAt: string;
  operator: string;
}

/** Narrow WIP projection for the list view — see listWipsSlim(). */
/**
 * Narrow sewer-dispatch projection for the Subcon Sewers roster and the
 * subcon-pending breakdown.
 *
 * The identifying columns below (order/serial/SKU/store/batch/DS) exist so the
 * breakdown grid can name each bundle a sewer is holding the way the BQC3
 * sheet names it. They are all WIP_CORE columns — no mother fields — so this
 * projection never needs WIP_SELECT's fallback.
 */
export interface ISewerViewWip {
  /**
   * The SharePoint item id — NOT the WIP number.
   *
   * Carried because `wipId` is not reliably unique: iSyn_WIPs can hold two
   * items for one WIP number (a double insert during import), and a consumer
   * that keys on wipId then double-counts the bundle and renders two rows
   * under one React key. This is the id that tells them apart, and the one
   * somebody needs in order to go and delete the wrong row.
   */
  spId: number;
  wipId: string;
  qty: number;
  sewer: string;
  currentStage?: CurrentStage;
  dispatchedAt?: string;
  receivedAt?: string;
  /** Import remarks — lets consumers exclude cancelled orders (isCancelledWip). */
  notes?: string;
  orderId?: string;
  serialId?: string;
  sku?: string;
  design?: string;
  storeChannel?: string;
  dueDate?: string;
  /** BQC3's own DS # — the dispatch slip the bundle left on. */
  assignedDS?: string;
  /** The lookup's raw id, NOT the batch code: resolving the code here would
   * mean $expand on a query that walks the whole list. See SEWER_VIEW_SELECT.
   * Resolve it against listBatches() — a few dozen rows. */
  batchId?: number;
  /** The product line, which is what costing is priced from — see
   * cogmRates.ts. iSyn_WIPs' own `cogm` column is deliberately NOT carried
   * here: reading subcon exposure off it produced ~P20 a set against a rate
   * card running P715-1080, so it measures something else. */
  category?: Category;
}

export interface ISlimWip {
  spId: number;
  wipId: string;
  /** The marketplace's own order key — what the order-status join runs on.
   *  Distinct from `wipId`, which is this factory's unit: one order covers
   *  several WIPs, and a buffer line carries "0". */
  orderId?: string;
  qty: number;
  /**
   * What the marketplace says became of this bundle's ORDER, joined on
   * ORDER ID after the read — see orderStatusJoin.
   *
   * DERIVED, NEVER A COLUMN. iSyn_WIPs has neither of these and must not: a
   * bulk write of somebody else's export may never reach the mother table.
   * Nothing may name them in a `$select`.
   */
  orderStatus?: "CANCELLED" | "COMPLETED";
  orderLabel?: "CANCELLED" | "SHIPPED" | "COMPLETED";
  orderCancelled?: boolean;
  dueDate?: string;
  currentStage?: CurrentStage;
  batchId?: number;
  dispatchedAt?: string;
  receivedAt?: string;
  /** Who sewed it — BQC4's manpower is the distinct sewers whose bundles came
   *  back that day, not the clerk booking them in. See liveSewers. Absent on
   *  a site reading the fallback projection; see WIP_SLIM_CORE. */
  assignedSewer?: string;
  /** When the IMPORT recorded this bundle's QC — the proof that BQC5's
   *  persisted figures already count it. Absent on a site whose list has not
   *  been given the column yet; see WIP_SLIM_CORE. */
  qcPassedAt?: string;
  storeChannel?: StoreChannel;
  /** BQC1's own planning date — where BQC2's APT clock starts. Absent on a
   *  site whose list has not been given the column yet; see WIP_SLIM_CORE. */
  dateAssignedToCutter?: string;
  /** When the IMPORT recorded this bundle's cut — the proof that BQC2's
   *  persisted figures already count it, and the cut-side twin of the two
   *  above. Absent on a site whose list has not been given the column yet;
   *  see WIP_SLIM_CORE. */
  cutAt?: string;
  /** Import remarks (e.g. "PACKED — …", "INBOUNDED — …") — drives the list view's Packed tab. */
  notes?: string;
  /**
   * What the bundle IS, which is the only thing that can price it.
   *
   * `sku` gives the parent (SS028-MDBLU-L → SS028) and so the exact rate when
   * it is a top SKU; `category` picks the band average when it is not. See
   * cogmBands.ts. Without these the grid can count a day's sets but cannot say
   * what they cost, which is what left the COST line at an em dash.
   *
   * In the CORE tier, not the SELECT one: both are long-standing columns that
   * SEWER_VIEW_SELECT already reads with no fallback at all, so naming them
   * cannot 400 a read the way a newly provisioned column would.
   */
  sku?: string;
  category?: Category;
}

interface ISpWipRow {
  Id: number;
  wipNumber?: string | null;
  orderId?: string | null;
  serialId?: string | null;
  cutter?: string | null;
  design?: string | null;
  sku?: string | null;
  category?: string | null;
  qty?: number | null;
  currentStage?: string | null;
  dateCreated?: string | null;
  dueDate?: string | null;
  storeChannel?: string | null;
  notes?: string | null;
  BatchIDId?: number | null;
  BatchID?: { Id: number; Title: string | null } | null;
  AssignedSewer?: string | null;
  color?: string | null;
  size?: string | null;
  assignedDS?: string | null;
  deliveryStatus?: string | null;
  receivedQty?: number | null;
  dispatchedAt?: string | null;
  receivedAt?: string | null;
  cutOperator?: string | null;
  Modified?: string | null;
  joSeries?: string | null;
  wkSeries?: string | null;
  dateAssignedToCutter?: string | null;
  inboundedToRtgStockroom?: boolean | null;
  packed?: boolean | null;
  cancellationStatus?: string | null;
  directMatl?: number | null;
  cogm?: number | null;
  grn?: string | null;
  qcPassedQty?: number | null;
  qcPassedAt?: string | null;
  cutAt?: string | null;
  qcPersonnel?: string | null;
  qcRepairQty?: number | null;
}

interface ISpBatchRow {
  Id: number;
  Title?: string | null;
  wkSeries?: string | null;
  dateOrdered?: string | null;
  prodPlan?: number | null;
  cutTotal?: number | null;
  assignedTotal?: number | null;
  receivedTotal?: number | null;
  qcTotal?: number | null;
  batchStatus?: string | null;
  notes?: string | null;
}

interface ISpStageHistoryRow {
  Id: number;
  wipId?: string | null;
  fromStage?: string | null;
  toStage?: string | null;
  transitionAt?: string | null;
  operator?: string | null;
}

const WIP_CORE_FIELDS =
  "Id,wipNumber,orderId,serialId,cutter,design,sku,category,qty,currentStage," +
  "dateCreated,dueDate,storeChannel,notes,BatchIDId,AssignedSewer," +
  "color,size,assignedDS,deliveryStatus,receivedQty,dispatchedAt,receivedAt,cutOperator,Modified";
/**
 * The batch lookup's own fields, which only exist under $expand.
 *
 * SEPARATED BECAUSE THE EXPAND IS THE EXPENSIVE PART. Expanding a lookup on a
 * list past the 5,000-item view threshold turns a paging read into a crawl —
 * PR #658 fixed exactly this on listWipsSewerView, where the subcon breakdown
 * sat on its loading line and never came back. iSyn_WIPs is 17,000 rows and
 * the same expand was still on the read every BQC1 screen makes on load.
 *
 * A full-list read therefore asks for BatchIDId, the raw lookup id, which
 * needs no expand, and resolves the CODE against the batch headers — 492 rows,
 * one page, fetched alongside. Same column, none of the cost. Only the small
 * reads (one batch, one WIP) still expand, where there is no paging to crawl.
 */
const WIP_BATCH_EXPANSION = "BatchID/Id,BatchID/Title";
const WIP_CORE = `${WIP_CORE_FIELDS},${WIP_BATCH_EXPANSION}`;
/** BQC1's own mother-record columns, added after iSyn_WIPs first went live —
 * a site that hasn't run "Add missing columns" (Production Grid Settings →
 * Maintenance) yet rejects the whole $select with these included. See the
 * WIP_CORE fallback in listWips()/listWipsForBatch() below — same pattern as
 * DispatchService's SLIP_SELECT/SLIP_CORE for the amend columns. */
/** The same names as WIP_SELECT's tail, as a list rather than a string —
 * shared with bulkImport()'s write-side fallback below (see wipBody). */
const WIP_MOTHER_FIELDS = [
  "joSeries", "wkSeries", "dateAssignedToCutter", "inboundedToRtgStockroom",
  "packed", "cancellationStatus", "directMatl", "cogm", "grn", "qcPassedQty", "qcPassedAt",
  "qcPersonnel", "qcRepairQty", "cutAt",
] as const;
const WIP_SELECT = WIP_CORE + "," + WIP_MOTHER_FIELDS.join(",");
/** The same pair again, without the lookup expansion — see WIP_BATCH_EXPANSION. */
const WIP_CORE_FLAT = WIP_CORE_FIELDS;
const WIP_SELECT_FLAT = WIP_CORE_FIELDS + "," + WIP_MOTHER_FIELDS.join(",");
const WIP_EXPAND = "BatchID";
/**
 * The list view's narrow projection, in the SELECT/CORE pair every read here
 * uses (CLAUDE.md: "never add a column to a $select in the same change that
 * introduces it" — SharePoint rejects the WHOLE $select when it names one
 * column a list does not have, so the read 400s rather than degrading).
 *
 * qcPassedAt and dateAssignedToCutter are the newer half. The first proves
 * the import has already counted a bundle's QC (see liveStageTopUp); the
 * second is BQC1's planning date, which is where BQC2's own APT clock
 * starts. A site that has not run "Add missing columns" simply reads without
 * them, which costs it the live BQC5 figure and the live cut APT, and
 * nothing else.
 *
 * assignedSewer rides in the SAME newer half, though the column itself is
 * old: BQC4's manpower is the sewers whose bundles came back, and without a
 * sewer per WIP the app cannot name them (see liveSewers). It is in the
 * SELECT rather than the CORE for the reason above — a site missing anything
 * named here loses the whole read, so the tier that can be dropped is the
 * only safe place to add to, and losing it costs only BQC4's live crew line.
 */
const WIP_SLIM_CORE = "Id,wipNumber,orderId,qty,dueDate,currentStage,BatchIDId,dispatchedAt,receivedAt,storeChannel,notes,sku,category";
const WIP_SLIM_SELECT = WIP_SLIM_CORE + ",qcPassedAt,dateAssignedToCutter,cutAt,AssignedSewer";
/**
 * listWipsSewerView's projection.
 *
 * WIP_CORE columns only, so it never hits the missing-mother-column rejection
 * WIP_SELECT has to fall back from — and DELIBERATELY NO $expand. This query
 * pages through every WIP in the list; expanding the BatchID lookup on a list
 * past the 5,000-item view threshold turns a fast read into one that crawls
 * page by page and looks, from the screen, exactly like a hang. Callers that
 * need the batch CODE resolve `batchId` against the batch headers, which are
 * a few dozen rows.
 */
const SEWER_VIEW_SELECT =
  "Id,wipNumber,qty,AssignedSewer,currentStage,dispatchedAt,receivedAt,notes," +
  "orderId,serialId,sku,design,storeChannel,dueDate,assignedDS,category,BatchIDId";

const BATCH_SELECT = "Id,Title,wkSeries,dateOrdered,prodPlan,cutTotal,assignedTotal,receivedTotal,qcTotal,batchStatus,notes";

const STAGE_HISTORY_SELECT = "Id,wipId,fromStage,toStage,transitionAt,operator";

const BATCH_SIZE = 100;
// Kept deliberately low: SharePoint aggressively throttles (HTTP 429) a large
// bulk import (thousands of WIP writes), and repeated same-day re-imports make
// it worse. Fewer parallel $batch requests = far less throttling. Raising this
// trades import speed for a much higher chance the whole import lands.
const BATCH_CONCURRENCY = 2;
// Courtesy pause after each chunk so we never burst SharePoint's rate limiter
// into a throttle spiral — a small, jittered gap drains its budget between
// writes. Cheap insurance vs. a partially-failed import.
const CHUNK_PAUSE_MS = 140;

/** SharePoint statuses worth retrying: 429/503 = throttled, 500/504 = the
 * server buckling under bulk load (usually transient, not a bad payload). */
function isRetryableStatus(msg: string): boolean {
  return /\b(429|500|503|504)\b/.test(msg) || /throttl/i.test(msg);
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

function guid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** What a $batch answered: one status per operation, and the first error it named. */
interface IBatchOutcome {
  statuses: number[];
  error: string | null;
}

/**
 * What to do after a chunk of updates.
 *
 * `systemic` non-null means every row was refused for one shared reason, so
 * there is nothing to retry and the caller should stop rather than repeat it.
 */
interface IChunkOutcome {
  retry: number[];
  systemic: string | null;
}

export class ProductionGridService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);
  private entityTypeCache = new Map<string, string>();
  private activeOperation: string | null = null;
  // StageHistory is an ever-growing audit list (28k+ rows). Above the 5k
  // list-view threshold, a $filter/$orderby on a NON-INDEXED column returns a
  // hard 500 — which the browser logs as a red console line even when we catch
  // the rejection. We must therefore NOT issue the filtered query until the
  // column is actually indexed. We can't ask SharePoint whether it's indexed
  // either: the field-metadata read (getbyinternalnameortitle?$select=Indexed)
  // itself 500s in this tenant, so probing just trades one red line for another.
  // Instead we track it with a purely LOCAL flag that the "Rebuild index"
  // maintenance action sets — no network call, so no possible console error.
  // Before a rebuild: flag absent -> always take the plain full-read path (works
  // at any size), caching the read so repeated batch-opens and the grid KPI read
  // share one fetch. After a rebuild: flag true -> use the fast indexed query.
  /**
   * Whether the filtered/ordered stage-history queries are usable.
   *
   * WAS a localStorage flag set by whoever pressed "Rebuild index", which made
   * the fast path a property of ONE BROWSER: the machine that ran the rebuild
   * got it, every station kept doing full-table reads for ever, and a private
   * window or a new laptop silently opted out. A fact about a SharePoint list
   * is not per-device state.
   *
   * Now it is simply learned. undefined = not tried this session; the first
   * filtered query settles it, and every caller already falls back safely
   * (unconditionally — see the catch blocks below). Costs at most one refused
   * request per page load while the list is un-indexed, and needs no storage,
   * no setting and no button: after a rebuild the next page load discovers the
   * fast path on its own, for everyone at once.
   */
  private historyFilterUsable?: boolean;
  private historyCache?: { at: number; rows: IStageHistoryEntry[] };
  private static readonly HISTORY_CACHE_MS = 60_000;

  /** Try the fast path until it is known not to work. */
  private stageHistoryIndexed(): boolean {
    return this.historyFilterUsable !== false;
  }

  /** Remember how the last filtered stage-history query went, so a session
   *  stops re-asking a question it already has the answer to. */
  private noteHistoryFilter(worked: boolean): void {
    this.historyFilterUsable = worked;
  }

  /** Called after a rebuild: the list is empty and indexed, so both the cached
   *  rows and the learned verdict are stale. */
  public markStageHistoryIndexed(): void {
    this.historyFilterUsable = undefined;
    this.historyCache = undefined;
  }

  private async cachedStageHistory(): Promise<IStageHistoryEntry[]> {
    const now = Date.now();
    if (this.historyCache && now - this.historyCache.at < ProductionGridService.HISTORY_CACHE_MS) {
      return this.historyCache.rows;
    }
    const rows = await this.listStageHistory();
    this.historyCache = { at: now, rows };
    return rows;
  }

  /**
   * Thrown when a write is refused because another one holds the lock. Typed so
   * callers can tell "an import is already running" — an ordinary, expected
   * state — apart from a genuine failure. Auto-sync used to surface it as a red
   * "Auto-sync error … This is the app failing to REACH the workbook", which is
   * both alarming and false: nothing had failed, and the workbook was fine.
   */
  public static readonly BUSY = "PG_WRITE_BUSY";

  /** Is a bulk write in progress? Survives a component remount, which the
   *  React-side guards cannot — see the adoption path in ProductionGrid. */
  public isBusy(): boolean {
    return this.activeOperation !== null;
  }

  /**
   * When the current write lock was acquired (ms epoch), or null if idle.
   *
   * Exists so the UI can show a duration that survives THIS TAB'S OWN
   * remounts. A component-local "since" timestamp, seeded fresh every time a
   * new mount adopts an already-running import via isBusy(), resets to
   * "just started" on every reload/navigation regardless of how long the
   * real import has actually been running — exactly the display bug that let
   * an hour-long stall keep reading "1m so far" on 2026-08-16.
   */
  public lockStartedAtMs(): number | null {
    return this.lockStartedAt;
  }

  /** Resolves when the in-flight write finishes (or immediately if none is).
   *  Never rejects: a caller waiting on someone else's import wants to know it
   *  ENDED, not to inherit its failure. */
  public whenIdle(): Promise<void> {
    return this.activePromise ? this.activePromise.then(() => undefined, () => undefined) : Promise.resolve();
  }

  private activePromise: Promise<unknown> | null = null;
  private lockStartedAt: number | null = null;

  /**
   * How long a lock may be held before the NEXT caller is let through anyway.
   *
   * A tab that goes to sleep mid-fetch (laptop lid closed, browser throttles a
   * background tab, a hung request with no timeout) can leave its promise
   * permanently pending — nothing in this class ever resolves or rejects it,
   * so `finally` never runs and the lock never clears. Every future import,
   * in every tab, then gets refused forever with "already running", and there
   * is no user-facing way to break that short of closing every open copy of
   * the app (2026-08-16: exactly this happened — Grid last imported sat stuck
   * for nearly an hour while Import kept refusing with "already running").
   * A generous timeout — the UI's own "a full import takes a few minutes"
   * claim, times several, for headroom on a slow connection — turns that
   * permanent jam into, at worst, one skipped cycle.
   */
  private static readonly STALE_LOCK_MS = 10 * 60_000;

  private async withWriteLock<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (this.activeOperation) {
      const heldMs = this.lockStartedAt !== null ? Date.now() - this.lockStartedAt : Infinity;
      if (heldMs < ProductionGridService.STALE_LOCK_MS) {
        const err = new Error(`"${this.activeOperation}" is already running. This one was skipped rather than run twice over the same lists.`);
        err.name = ProductionGridService.BUSY;
        throw err;
      }
      // The previous holder never released it — treat it as abandoned rather
      // than let it block imports indefinitely.
      console.warn(`[ProductionGridService] "${this.activeOperation}" has held the write lock for ${Math.round(heldMs / 60000)}m without finishing — treating it as abandoned and starting "${label}".`);
    }
    this.activeOperation = label;
    this.lockStartedAt = Date.now();
    const run = (async () => {
      try {
        return await fn();
      } finally {
        this.activeOperation = null;
        this.lockStartedAt = null;
        this.activePromise = null;
      }
    })();
    this.activePromise = run;
    return run;
  }

  // ─── READ ────────────────────────────────────────────────────────────
  // LIST VIEW THRESHOLD NOTE: once a list passes 5,000 items, SharePoint
  // rejects any query that sorts or filters on a NON-INDEXED column with
  // SPQueryThrottledException ("exceeds the list view threshold") — a plain
  // paged read (no $orderby/$filter) always works. So the bulk reads below
  // never use $orderby (client-side sort instead), and the filtered reads
  // rely on the indexes GridProvisioningService creates (with a client-side
  // full-read fallback in case an index couldn't be created).

  /**
   * Reads WIPs with WIP_SELECT, falling back to WIP_CORE (the columns that
   * predate BQC1's mother-record fields) on a "does not exist" 400 — same
   * pattern as DispatchService's SLIP_SELECT/SLIP_CORE for the amend
   * columns. Without this, every WIP-reading screen in the app (Dispatch,
   * Receiving, QC, Control Tower) hard-fails with a raw SharePoint error the
   * moment WIP_SELECT names a column a site hasn't provisioned yet — which is
   * exactly what shipping WIP_SELECT ahead of "Add missing columns" actually
   * being run does. The newer fields just come back undefined until then.
   */
  /**
   * @param opts.expandBatch Expand the BatchID lookup so `BatchID.Title` comes
   * back with the row. Only for reads that return a SMALL, filtered result —
   * on a full-list page-through it is what turns the read into a crawl (see
   * WIP_BATCH_EXPANSION). Full reads pass false and resolve the code from the
   * batch headers instead.
   */
  private async readWipRows(
    opts: { filter?: string; top: number; expandBatch?: boolean; onPage?: (n: number, page: ISpWipRow[]) => void },
  ): Promise<ISpWipRow[]> {
    const { expandBatch = false, ...query } = opts;
    const select = expandBatch ? WIP_SELECT : WIP_SELECT_FLAT;
    const expand = expandBatch ? WIP_EXPAND : undefined;
    try {
      return await this.sp.getListItems<ISpWipRow>(WIPS_LIST, { select, expand, ...query });
    } catch (e) {
      // A site that hasn't run "Add missing columns" yet rejects the whole
      // $select the moment WIP_SELECT names a column it doesn't have —
      // unconditional fallback, same as DispatchService's SLIP_SELECT/
      // SLIP_CORE, rather than pattern-matching SharePoint's exact wording.
      console.warn(
        "[ProductionGridService] WIP read without the newer BQC1 columns (joSeries/wkSeries/dateAssignedToCutter/" +
        "inboundedToRtgStockroom/packed/cancellationStatus/directMatl/cogm/grn/qcPassedQty/qcPassedAt/" +
        "qcPersonnel/qcRepairQty) — run " +
        "\"Add missing columns\" in Production Grid Settings → Maintenance to add them (\"Set up lists\" only " +
        "creates whole lists, it never adds a column to one that already exists).",
        e
      );
      return this.sp.getListItems<ISpWipRow>(WIPS_LIST, {
        select: expandBatch ? WIP_CORE : WIP_CORE_FLAT, expand, ...query,
      });
    }
  }

  /**
   * Every WIP, full fidelity.
   *
   * ON THE PAGE-LOAD PATH, whatever the old comment here claimed:
   * useWorkcenterData calls it for every BQC1 screen, as do Gate 1, What-If,
   * Sewer Metrics and the four outbox services. 17,000 rows at 5,000 to a
   * page is four sequential round trips, and it used to expand the BatchID
   * lookup on top of that — the exact thing that hung the subcon breakdown in
   * PR #658, left on the read the whole product opens with.
   *
   * The expand is gone. `batchCode` is resolved against the batch headers
   * instead, which is one extra request for a few hundred rows, fetched
   * alongside rather than after — so the read is no slower even when the
   * WIP pages come back instantly.
   */
  public async listWips(
    onPage?: (rows: IProductionWip[], soFar: number) => void,
  ): Promise<IProductionWip[]> {
    /**
     * `onPage` HANDS OVER EACH PAGE AS IT LANDS, so a screen can paint the
     * first five thousand while the other six requests are still in flight.
     *
     * 34,659 WIPs at SharePoint's 5,000-row ceiling is seven SEQUENTIAL round
     * trips — the pager follows `__next`, so they cannot overlap — and the
     * BQC1 board sat on "Reading the plan…" for all seven (Mikko, 2026-09-18:
     * "it took so long to load this BQC1 data"). The rows were arriving in
     * sevenths the whole time and being thrown on a pile.
     *
     * THE PAGES ARE NOT SORTED. The finished array still comes back sorted by
     * WIP id, as it always has, and every existing caller gets exactly that; a
     * progressive caller is handed rows in the order SharePoint returned them
     * and sorts for itself — which the board does anyway, by DATE.
     *
     * `batchCode` is resolved from the header map, which is fetched alongside
     * and may not have landed when page one does. A page emitted before it
     * carries no batch code, and the final array carries them all. That is
     * safe for the board, which never reads it; a caller that DOES read it
     * should not use `onPage`.
     */
    const codesPromise = this.batchCodes();
    // Held in a local as soon as it resolves, so a page that lands after it
    // carries its batch codes and one that lands before simply does not.
    let partialCodes: ReadonlyMap<number, string> | undefined;
    void codesPromise.then((c) => { partialCodes = c; });

    const [rows, codes] = await Promise.all([
      this.readWipRows({
        top: 5000,
        onPage: onPage
          ? (soFar, page) => onPage(page.map((r) => this.mapWip(r, partialCodes)), soFar)
          : undefined,
      }),
      codesPromise,
    ]);
    return rows.map((r) => this.mapWip(r, codes)).sort((a, b) => a.wipId.localeCompare(b.wipId));
  }

  /**
   * The WIPs FOR SUBCONTRACTORS · PAYROLL — the few columns payroll reads,
   * fetched in parallel Id ranges.
   *
   * Payroll needs a WIP's number, serial, design, SKU, category, qty and
   * channel, and nothing else. The full read carries ~40 columns for each of
   * 34,000 rows through seven SEQUENTIAL pages, and the Subcon Payroll screen
   * sat on "Still reading: WIPs" for 18 seconds and more (Mikko, 2026-09-30:
   * "still experiencing this one"). Every column named here is in
   * WIP_CORE_FIELDS — read by every WIP query since the list existed — so this
   * select cannot 400 on a site missing the newer columns.
   *
   * Any failure of the ranged read falls back to the same slim select through
   * the ordinary pager: slower, never wrong.
   */
  public async listWipsForPayroll(): Promise<IProductionWip[]> {
    const select = "Id,wipNumber,serialId,design,sku,category,qty,storeChannel";
    let rows: ISpWipRow[];
    try {
      const top = await this.sp.getListItems<{ Id: number }>(WIPS_LIST, {
        select: "Id", orderby: "Id desc", top: 1, singlePage: true,
      });
      const maxId = top[0]?.Id ?? 0;
      const pages = await mapPool(idRanges(maxId, 4000), 5, ({ lo, hi }) =>
        this.sp.getListItems<ISpWipRow>(WIPS_LIST, {
          select, filter: `Id gt ${lo} and Id le ${hi}`, top: 5000,
        }));
      rows = pages.flat();
    } catch (e) {
      console.warn("[ProductionGridService] ranged WIP read failed — reading the payroll columns page by page.", e);
      rows = await this.sp.getListItems<ISpWipRow>(WIPS_LIST, { select, top: 5000 });
    }
    return rows.map((r) => this.mapWip(r)).sort((a, b) => a.wipId.localeCompare(b.wipId));
  }

  /**
   * Batch id → batch code, for rows read without the lookup expansion.
   *
   * A failed read costs the batch CODE and nothing else, so it degrades to an
   * empty map rather than failing every WIP on the screen — but it says so,
   * because a column that quietly empties looks like data that was never
   * there. Cached for the same minute the stage history is: the headers are a
   * few hundred rows that change when a batch is created, and every screen on
   * a page load asks for the same answer.
   */
  private batchCodeCache?: { at: number; codes: Map<number, string> };

  private async batchCodes(): Promise<Map<number, string>> {
    const now = Date.now();
    if (this.batchCodeCache && now - this.batchCodeCache.at < ProductionGridService.HISTORY_CACHE_MS) {
      return this.batchCodeCache.codes;
    }
    try {
      const headers = await this.listBatches();
      const codes = batchCodeIndex(headers);
      this.batchCodeCache = { at: now, codes };
      return codes;
    } catch (e) {
      console.warn(
        "[ProductionGridService] batch headers unreadable — WIP rows will carry no batch code. " +
        "Everything else about them is intact.", e);
      return new Map();
    }
  }

  /**
   * Narrow projection for the list view — just enough fields to compute
   * per-batch WIP counts/overdue/tier and the KPI-vs-yesterday join, no
   * $expand. The list view previously called listWips() (every field, every
   * WIP, every page load) — this is what made it slow once the list holds
   * more than a handful of rows. Batch Detail's rich per-WIP display uses
   * listWipsForBatch() instead, filtered to one batch.
   */
  public async listWipsSlim(): Promise<ISlimWip[]> {
    let rows: ISpWipRow[];
    try {
      rows = await this.sp.getListItems<ISpWipRow>(WIPS_LIST, {
        select: WIP_SLIM_SELECT, top: 5000,
      });
    } catch (e) {
      // Unconditional fallback, same posture as readWipRows and
      // DispatchService's SLIP_SELECT/SLIP_CORE: a site without qcPassedAt
      // must lose the live BQC5 top-up, not the whole grid.
      console.warn(
        "[ProductionGridService] slim WIP read without qcPassedAt/" +
        "dateAssignedToCutter — run \"Add missing columns\" in Production Grid " +
        "Settings → Maintenance to add them. Until then BQC5's live figure " +
        "cannot tell an imported QC from a fresh one, and BQC2 has no start " +
        "date to measure its APT from, so both are left off.",
        e
      );
      rows = await this.sp.getListItems<ISpWipRow>(WIPS_LIST, {
        select: WIP_SLIM_CORE, top: 5000,
      });
    }
    return rows.map((r) => ({
      spId: r.Id,
      wipId: r.wipNumber ?? String(r.Id),
      // The marketplace's own key, and the only thing an order-status join can
      // run on — see orderStatusJoin. In the CORE tier for the same reason sku
      // and category are: WIP_SELECT has read it with no fallback since the
      // list existed, so naming it here cannot 400 a read.
      orderId: r.orderId ?? undefined,
      qty: r.qty ?? 0,
      dueDate: r.dueDate ?? undefined,
      currentStage: (r.currentStage as CurrentStage) ?? undefined,
      batchId: r.BatchIDId ?? undefined,
      dispatchedAt: r.dispatchedAt ?? undefined,
      receivedAt: r.receivedAt ?? undefined,
      qcPassedAt: r.qcPassedAt ?? undefined,
      dateAssignedToCutter: r.dateAssignedToCutter ?? undefined,
      cutAt: r.cutAt ?? undefined,
      assignedSewer: r.AssignedSewer ?? undefined,
      storeChannel: (r.storeChannel as StoreChannel) ?? undefined,
      notes: r.notes ?? undefined,
      sku: r.sku ?? undefined,
      category: (r.category as Category) ?? undefined,
    }));
  }

  /**
   * Sewer-view projection: which sewer each WIP is dispatched to, and whether
   * it has come back. Used by the BQC4 Smart Insights' Subcon Sewers tab —
   * subcontractors have no HRIS attendance, so their "roster" is built from
   * the BQC3 dispatch data itself.
   */
  public async listWipsSewerView(): Promise<ISewerViewWip[]> {
    const rows = await this.sp.getListItems<ISpWipRow>(WIPS_LIST, {
      select: SEWER_VIEW_SELECT, top: 5000,
    });
    return rows
      .filter((r) => (r.AssignedSewer ?? "").trim() !== "")
      .map((r) => ({
        spId: r.Id,
        wipId: r.wipNumber ?? String(r.Id),
        qty: r.qty ?? 0,
        sewer: (r.AssignedSewer ?? "").trim(),
        currentStage: (r.currentStage as CurrentStage) ?? undefined,
        dispatchedAt: r.dispatchedAt ?? undefined,
        receivedAt: r.receivedAt ?? undefined,
        notes: r.notes ?? undefined,
        orderId: r.orderId ?? undefined,
        serialId: r.serialId ?? undefined,
        sku: r.sku ?? undefined,
        design: r.design ?? undefined,
        storeChannel: r.storeChannel ?? undefined,
        dueDate: r.dueDate ?? undefined,
        assignedDS: r.assignedDS ?? undefined,
        batchId: r.BatchIDId ?? undefined,
        category: (r.category as Category) ?? undefined,
      }));
  }

  /** Full-fidelity WIPs for exactly one batch — used by Batch Detail, not the list view. */
  public async listWipsForBatch(batchId: number): Promise<IProductionWip[]> {
    try {
      const rows = await this.readWipRows({ filter: `BatchIDId eq ${batchId}`, top: 5000, expandBatch: true });
      return rows.map((r) => this.mapWip(r)).sort((a, b) => a.wipId.localeCompare(b.wipId));
    } catch (err) {
      // BatchID not indexed yet (list view threshold) — fall back to a plain
      // paged full read filtered client-side. Heavier, but always works.
      // UNCONDITIONAL, not matched against SharePoint's wording. An unindexed
      // column past the list-view threshold comes back as a bare 500 with no
      // useful body, so a guard that looked for "threshold" or "throttled"
      // never fired and the whole panel failed instead of degrading. Same rule
      // CLAUDE.md already states for a rejected $select: fall back, don't
      // pattern-match. The filtered read is an optimisation; losing it costs
      // time, and losing the data costs the screen.
      console.warn("[ProductionGridService] per-batch WIP filter failed; reading the full list.", err);
      const all = await this.listWips();
      return all.filter((w) => w.batchId === batchId);
    }
  }

  /**
   * Live, single-WIP lookup straight against iSyn_WIPs — no page-load
   * snapshot, no local cache. A scanning screen's `wips` state loads once
   * per visit; the background workbook sync can add a brand-new WIP to
   * iSyn_WIPs (BQC1's own mother record) at any point after that. A scan
   * that misses the in-memory list should check here — the live list —
   * before refusing, rather than trusting however stale that snapshot has
   * become. iSyn_WIPs is the source of truth; a screen's own state is only
   * ever a copy of it.
   */
  public async getWipByWipId(wipId: string): Promise<IProductionWip | null> {
    const id = wipId.trim();
    if (!id) return null;
    const filter = `wipNumber eq '${id.replace(/'/g, "''")}'`;
    const rows = await this.readWipRows({ filter, top: 1, expandBatch: true });
    return rows.length ? this.mapWip(rows[0]) : null;
  }

  public async listBatches(): Promise<IBatchHeader[]> {
    const rows = await this.sp.getListItems<ISpBatchRow>(BATCHES_LIST, { select: BATCH_SELECT, top: 5000 });
    return rows.map((r) => this.mapBatch(r));
  }

  /** Full-fidelity, unfiltered — only used by bulkImport()'s de-dup check, never on page load. */
  public async listStageHistory(): Promise<IStageHistoryEntry[]> {
    const rows = await this.sp.getListItems<ISpStageHistoryRow>(STAGE_HISTORY_LIST, {
      select: STAGE_HISTORY_SELECT, top: 5000,
    });
    return rows.map((r) => this.mapStageHistory(r));
  }

  /**
   * Latest `transitionAt` day present in the whole history table. Imported/
   * backfilled data is dated to when the real-world event happened, not to
   * whatever day the import ran — so the KPI tiles' "today/yesterday" must
   * anchor off this instead of the wall-clock date, or every tile reads 0
   * whenever nobody has transitioned a WIP on the literal current calendar day.
   */
  public async getLatestStageHistoryDate(): Promise<string | undefined> {
    // Until the list is rebuilt with indexes, $orderby=transitionAt on this
    // >5k-row list 500s (list view threshold) — skip it so no red line reaches
    // the console. This is only a KPI fallback anchor; undefined is fine.
    if (!this.stageHistoryIndexed()) return undefined;
    // Deliberately NOT using getListItems() here — it auto-follows `__next`
    // until exhausted, and SharePoint's REST API is known to break server-side
    // paging continuation when `$orderby` is combined with a small `$top`
    // (the skiptoken can fail to terminate), which hung the whole list view's
    // load forever. This is a single-page, one-shot fetch instead.
    try {
      const qs = `?$select=transitionAt&$orderby=transitionAt desc&$top=1`;
      const res = await this.sp.raw<{ d: { results: ISpStageHistoryRow[] } }>(
        `/_api/web/lists/getbytitle('${encodeURIComponent(STAGE_HISTORY_LIST)}')/items${qs}`
      );
      const row = res.d.results[0];
      return row ? this.mapStageHistory(row).transitionAt.slice(0, 10) : undefined;
    } catch {
      // transitionAt not indexed yet on a >5k-row list (list view threshold)
      // — this is only the KPI fallback anchor, never worth breaking the page.
      return undefined;
    }
  }

  /**
   * Filtered to `transitionAt >= sinceIso` — the list view only needs
   * today/yesterday for its KPI tiles, not the whole ever-growing audit
   * trail. Keeps this query small and fast regardless of how much history
   * has accumulated.
   */
  public async listStageHistorySince(sinceIso: string): Promise<IStageHistoryEntry[]> {
    // Until the list is rebuilt with indexes, the filtered query would 500 on
    // this big list — skip it entirely and filter a cached full read in memory,
    // so nothing red ever reaches the console.
    if (!this.stageHistoryIndexed()) {
      const all = await this.cachedStageHistory();
      return all.filter((h) => h.transitionAt >= sinceIso)
        .sort((a, b) => a.transitionAt.localeCompare(b.transitionAt));
    }
    try {
      const rows = await this.sp.getListItems<ISpStageHistoryRow>(STAGE_HISTORY_LIST, {
        select: STAGE_HISTORY_SELECT, filter: `transitionAt ge datetime'${sinceIso}T00:00:00Z'`, top: 5000,
      });
      return rows.map((r) => this.mapStageHistory(r)).sort((a, b) => a.transitionAt.localeCompare(b.transitionAt));
    } catch (err) {
      // transitionAt not indexed yet — KPI fallback only; return empty rather
      // than breaking the page (daily-output aggregates are the real source).
      // Unconditional for the same reason as the others in this file: a bare
      // 500 carries no wording to match on, and this path already degrades
      // safely (the daily-output aggregates are the real KPI source).
      this.noteHistoryFilter(false);
      console.warn("[ProductionGridService] stage-history filter failed; continuing without it.", err);
      return [];
    }
  }

  /**
   * Stage history for exactly the given WIP ids (Batch Detail's "time in
   * stage" / pipeline stepper) — chunked OR filter (40 ids/request, run in
   * parallel) rather than one request per WIP or the whole table.
   */
  public async listStageHistoryForWipIds(wipIds: string[]): Promise<IStageHistoryEntry[]> {
    if (wipIds.length === 0) return [];
    // Until the list is rebuilt with indexes, the chunked OR-filter would 500 on
    // this big list — skip it and filter a cached full read in memory.
    if (!this.stageHistoryIndexed()) {
      const wanted = new Set(wipIds);
      const all = await this.cachedStageHistory();
      return all.filter((h) => wanted.has(h.wipId))
        .sort((a, b) => a.transitionAt.localeCompare(b.transitionAt));
    }
    try {
      const CHUNK = 40;
      const chunks: string[][] = [];
      for (let i = 0; i < wipIds.length; i += CHUNK) chunks.push(wipIds.slice(i, i + CHUNK));
      const results = await Promise.all(chunks.map((chunk) => {
        const filter = chunk.map((id) => `wipId eq '${id.replace(/'/g, "''")}'`).join(" or ");
        return this.sp.getListItems<ISpStageHistoryRow>(STAGE_HISTORY_LIST, {
          select: STAGE_HISTORY_SELECT, filter, top: 5000,
        });
      }));
      this.noteHistoryFilter(true);
      return results.flat().map((r) => this.mapStageHistory(r))
        .sort((a, b) => a.transitionAt.localeCompare(b.transitionAt));
    } catch (err) {
      // wipId not indexed yet — fall back to a plain paged full read filtered
      // client-side (heavier, but immune to the list view threshold). The 28k-row
      // list can't be indexed, so the OR filter returns a 500 rather than a
      // worded threshold error — treat any 5xx / "Internal Server" as the trigger.
      // Unconditional for the same reason as the others in this file: a bare
      // 500 carries no wording to match on, and this path already degrades
      // safely (the daily-output aggregates are the real KPI source).
      this.noteHistoryFilter(false);
      console.warn("[ProductionGridService] stage-history filter failed; continuing without it.", err);
      const wanted = new Set(wipIds);
      const all = await this.listStageHistory();
      return all.filter((h) => wanted.has(h.wipId))
        .sort((a, b) => a.transitionAt.localeCompare(b.transitionAt));
    }
  }

  private mapStageHistory(r: ISpStageHistoryRow): IStageHistoryEntry {
    return {
      spId: r.Id,
      wipId: r.wipId ?? "",
      fromStage: (r.fromStage as StageChoice) ?? undefined,
      toStage: (r.toStage as StageChoice | "DONE") ?? "BQC1",
      transitionAt: r.transitionAt ?? "",
      operator: r.operator ?? "",
    };
  }

  // ─── BULK UPSERT — Batches, then WIPs (needs batch Ids), then StageHistory ──
  public async bulkImport(
    batches: IBatchHeader[],
    wips: IProductionWip[],
    history: IStageHistoryEntry[],
    onProgress?: (phase: string, done: number, total: number) => void,
    /**
     * `preserveSewer` omits AssignedSewer from the update body and from the
     * change comparison, so a run leaves whatever SharePoint already holds.
     *
     * Used when the app owns the column (valve at `live`) but its assignment
     * ledger could not be read — see importOwnership.ts. Writing the workbook's
     * value in that state would silently revert the Assignment Board.
     */
    /**
     * `planOnly` is a BQC1 Prod Plan (JO sheet) run — the mother record
     * refreshed from the Fulfillment handover alone, without the BQC2..BQC5
     * sheets the full import reads.
     *
     * It narrows the run in three ways, all for the same reason: this
     * workbook has no opinion about anything downstream of BQC1, and the
     * parser renders "no opinion" as an empty field, which `wipBody` sends as
     * `null` — clearing the column. See joPlanImport.ts for the field
     * classification and what one unguarded run would have blanked.
     *
     *   batches  created when missing, never updated (their rollup is not in
     *            this file, so every total would be written as 0)
     *   WIPs     created in full; UPDATED only in the columns the sheet owns
     *   history  not written at all — a plan records no stage transition
     */
    opts?: { preserveSewer?: boolean; planOnly?: boolean }
  ): Promise<{ batches: number; wips: number; history: number; errors: string[]; failedWipIds: string[] }> {
    const preserveSewer = opts?.preserveSewer ?? false;
    const planOnly = opts?.planOnly ?? false;
    return this.withWriteLock("Import to SharePoint", async () => {
      const errors: string[] = [];
      /**
       * WIPs the import could not write.
       *
       * Returned rather than folded into `errors` because the ids are what a
       * person needs: a WIP that fails here is absent from iSyn_WIPs and will
       * be refused at every scanning station, and until now the only trace was
       * an unattributed SharePoint message inside a count.
       */
      const failedWipIds: string[] = [];

      /**
       * Which columns each list is actually missing, read ONCE and up front.
       *
       * This used to run after the batches were already written, and then only
       * ever consulted the iSyn_WIPs entry — so a column missing from
       * iSyn_Batches was invisible to the very check that could see it, while
       * SharePoint refused all hundred writes with a 400 whose message names
       * exactly that as a possible cause. The probe covers every list it
       * provisions; there was no reason to ask it about one.
       *
       * A probe that THREW is not a probe that found nothing — see the WIP step
       * below, which still stops rather than guessing. Batches are more
       * forgiving: they are written first and a stale batch header is not the
       * end of an import, so a failed probe here degrades to writing every
       * field, which is exactly the behaviour that has always been in place.
       */
      let schemaGap: { list: string; fields: string[] }[] | null = null;
      try {
        schemaGap = await gridProvisioningService.missingFields();
      } catch (e) {
        errors.push(
          `Could not check whether the lists have every column this import writes ` +
          `(${(e as Error).message}).`
        );
      }
      const gapFor = (list: string): Set<string> | undefined => {
        const g = schemaGap?.find((x) => x.list === list);
        if (!g) return undefined;
        errors.push(
          `${list} is missing ${g.fields.length} column(s) (${g.fields.join(", ")}) — ` +
          "omitted this run so the rest of every row still imports. Add them in Production Grid Settings → " +
          "Maintenance → \"Add missing columns\", then re-import to backfill."
        );
        return new Set(g.fields);
      };
      const batchOmit = gapFor(BATCHES_LIST);

      // 1) Batches — upsert by batchCode (Title), get real Ids back.
      const existingBatches = await this.listBatches();
      const batchIdByCode = new Map<string, number>(existingBatches.map((b) => [b.batchCode, b.spId!]));
      const batchesToCreate = batches.filter((b) => !batchIdByCode.has(b.batchCode));
      /**
       * A PLAN RUN UPDATES prodPlan AND NOTHING ELSE ON A BATCH.
       *
       * It used to update nothing at all, because a JO sheet knows none of the
       * cut/assigned/received/QC rollup and writing those would zero it on
       * every batch the plan mentions. That reasoning holds for the rollups
       * and was over-applied to the whole header: `prodPlan` IS the JO sheet —
       * the sum of the plan's own quantities, and the denominator of every
       * Output % and Pending % on the grid.
       *
       * Left to the full import, a batch that gained rows kept a stale
       * denominator and every percentage on its row read high, with the only
       * remedy being an import that overwrites the stations (2026-09-14). So
       * the plan run now owns the one column it is actually the authority on,
       * and `planBatchOmitFields` withholds the rest.
       */
      const batchesToUpdate = batches.filter((b) => batchIdByCode.has(b.batchCode));
      const batchUpdateOmit = planOnly ? planBatchOmitFields(batchOmit) : batchOmit;

      const batchEntityType = await this.getEntityType(BATCHES_LIST);
      let batchCount = 0;
      // Same as the WIP phase: count against what will be WRITTEN. A plan run
       // creates two batches out of 303 in the file, and "2/303" reads as
       // "2 done of 303 to go" when it means "2 created, 303 referenced".
      const batchesToWrite = batchesToCreate.length + batchesToUpdate.length;
      batchCount += await this.runCreateChunked(
        BATCHES_LIST, batchesToCreate, (b) => this.batchBody(b, batchEntityType, batchOmit), errors,
        undefined, undefined, (done) => onProgress?.("batches", done, batchesToWrite),
      );

      const updatePairs = batchesToUpdate.map((b) => ({ id: batchIdByCode.get(b.batchCode)!, body: b }));
      batchCount += await this.runUpdateChunked(BATCHES_LIST, updatePairs, (b) => this.batchBody(b, batchEntityType, batchUpdateOmit), errors);
      if (onProgress) onProgress("batches", batchCount, batchesToWrite);

      // $batch creates don't hand back the new item's Id (would need to parse
      // each multipart sub-response body) — simplest reliable way to get real
      // Ids for the just-created batches is to re-read them by code.
      if (batchesToCreate.length > 0) {
        const refreshed = await this.listBatches();
        for (const b of refreshed) if (b.spId) batchIdByCode.set(b.batchCode, b.spId);
      }

      // 2) WIPs — upsert by wipId (wipNumber/Title), resolving batchId from step 1.
      const existingWips = await this.listWips();
      const wipByNumber = new Map(existingWips.map((w) => [w.wipId, w]));
      const wipEntityType = await this.getEntityType(WIPS_LIST);

      // If this site hasn't run "Add missing columns" yet, EVERY WIP write
      // that names one of the mother-record fields gets rejected with a 400
      // — SharePoint won't accept a field the list doesn't have. Left alone,
      // that means every create/update in this run fails individually (the
      // $batch attempt fails, falls back to one-by-one, and every one of
      // those fails too), burning minutes on writes that were always going to
      // lose and leaving iSyn_WIPs exactly as stale as before. A single
      // read-only check up front (no schema change — see GridProvisioningService's
      // own "only ever run explicitly from Settings" rule for addMissingFields)
      // lets this run omit just those fields and actually write everything
      // else, same as the read side already degrades via WIP_CORE.
      // A probe that THREW is not a probe that found nothing. Collapsing the
      // failure into "no columns missing" is what lets a run write a field the
      // list does not have — and SharePoint then refuses every row with 400,
      // which the chunked writer used to answer by rewriting all of them one
      // at a time. Treat an unreadable schema as a stop, not a green light.
      // The batch rows above already landed — report them, rather than saying
      // nothing was written when something was. Still a hard stop for WIPs: a
      // WIP written with a field the list does not have fails ENTIRELY, and a
      // WIP absent from iSyn_WIPs is refused at every scanning station.
      if (schemaGap === null) {
        errors.push(`Skipped the WIP write rather than send fields ${WIPS_LIST} may not have.`);
        return { errors, batches: batchCount, wips: 0, history: 0, failedWipIds };
      }
      const omitFields = gapFor(WIPS_LIST);
      /**
       * The UPDATE body's omit set, which on a plan run is much wider than
       * the schema gap — see joPlanImport.ts.
       *
       * CREATES DELIBERATELY KEEP `omitFields`, the narrow one. A WIP
       * appearing on the JO sheet for the first time genuinely has not been
       * cut, dispatched, received or QC'd: empty there is the truth about a
       * bundle handed over this morning, not a gap in the evidence. Omitting
       * currentStage on a create would leave a new row with no stage at all,
       * which every screen reads as neither BQC1 nor anything else.
       */
      /**
       * A FULL IMPORT NO LONGER ANSWERS FOR THE STATIONS (2026-09-14).
       *
       * BQC2..BQC5 write to SharePoint directly and the scheduled sync stopped
       * on 2026-09-08, so on those columns the workbook is not a second
       * opinion — it is a snapshot from before the work happened. An update
       * carrying its values rolls the bundle back to the file's last save.
       *
       * Creates still write the full body: a WIP the app has never seen has no
       * native state to protect. See STATION_OWNED_WIP_FIELDS.
       */
      const updateOmitFields = planOnly
        ? planOnlyOmitFields(omitFields)
        : fullImportOmitFields(omitFields);

      const toCreate: IProductionWip[] = [];
      const toUpdate: { id: number; wip: IProductionWip }[] = [];
      for (const w of wips) {
        const resolvedBatchId = w.batchCode ? batchIdByCode.get(w.batchCode) : undefined;
        const wip = { ...w, batchId: resolvedBatchId };
        const existing = wipByNumber.get(w.wipId);
        if (!existing) toCreate.push(wip);
        // The comparison uses the SAME set as the write. A field the body will
        // omit must not be able to mark a row as changed, or every plan run
        // queues an update whose only difference is a value it is not going to
        // send — thousands of pointless writes into SharePoint's throttle.
        else if (this.wipChanged(existing, wip, preserveSewer, updateOmitFields)) toUpdate.push({ id: existing.spId, wip });
      }

      let wipCount = 0;
      // Creates always carry the workbook's sewer: a row that does not exist yet
      // cannot have an app assignment (the app only ever assigns WIPs it read
      // from this list), so there is nothing to preserve and blanking it would
      // just lose the value until the next successful sync.
      const wipLabel = (w: IProductionWip): string => `WIP ${w.wipId}`;
      /**
       * The TOTAL a progress line should count against is what this run will
       * actually write — creates plus the updates that survived wipChanged —
       * not `wips.length`, which is every row the file covers. On a plan run
       * those differ by an order of magnitude ("2/16,012" against 137 real
       * writes), and a bar that never leaves 0% is worse than no bar.
       */
      const willWrite = toCreate.length + toUpdate.length;
      let written = 0;
      const reportWips = (): void => onProgress?.("wips", written, willWrite);
      reportWips();
      wipCount += await this.runCreateChunked(
        WIPS_LIST, toCreate, (w) => this.wipBody(w, wipEntityType, false, omitFields), errors, wipLabel, failedWipIds,
        (done) => { written = done; reportWips(); },
      );
      const createdSoFar = written;
      wipCount += await this.runUpdateChunked(
        WIPS_LIST, toUpdate.map((u) => ({ id: u.id, body: u.wip })),
        (w) => this.wipBody(w, wipEntityType, preserveSewer, updateOmitFields), errors, wipLabel, failedWipIds,
        (done) => { written = createdSoFar + done; reportWips(); },
      );
      if (onProgress) onProgress("wips", wipCount, willWrite);

      /**
       * A PLAN RUN WRITES NO STAGE HISTORY AND STOPS HERE.
       *
       * Every entry the parser derives from a JO-only workbook is a BQC1
       * transition, because that is the only sheet in the file. Appending
       * those would put a fresh "→ BQC1" row against bundles that are at
       * BQC4, and the prune below would then be deciding what to delete on
       * the strength of it.
       */
      if (planOnly) {
        return { errors, batches: batchCount, wips: wipCount, history: 0, failedWipIds };
      }

      // 3) Stage history — append-only, de-duped against existing (wipId, toStage)
      // pairs, and CAPPED. See STAGE_HISTORY_RETENTION_DAYS: this table is fully
      // derived, and letting it grow without limit is what eventually stopped it
      // accepting writes at all.
      const historyCutoff = retentionCutoff(Date.now(), STAGE_HISTORY_RETENTION_DAYS);
      const existingHistory = await this.listStageHistory();
      const seen = new Set(existingHistory.map((h) => `${h.wipId}|${h.toStage}`));
      // Outside the window a row would be pruned on the very next run, so there
      // is no sense writing it — and on a first import after a rebuild that is
      // most of the table.
      const newHistory = withinRetention(history, historyCutoff)
        .filter((h) => !seen.has(`${h.wipId}|${h.toStage}`));
      /**
       * PRUNE FIRST, THEN WRITE — the order matters more than it looks.
       *
       * A list over SharePoint's 5,000-item view threshold refuses writes; the
       * deletes below are addressed by item id, which is not a query and works
       * regardless of size. Pruning afterwards meant every import first spent
       * itself failing hundreds of creates one at a time (the $batch falls back
       * to singles) and only then shrank the list — so a table with a backlog
       * took far longer to dig itself out, and each run paid the full cost of
       * writes that were always going to be refused.
       *
       * This way each import shrinks the table before asking anything of it,
       * and the run that finally drops it under the threshold gets its writes
       * accepted in the same pass rather than the next one.
       *
       * Never allowed to cost the import: rows outliving their window are
       * housekeeping, not a reason to report a failed sync.
       */
      try {
        const stale = staleRows(existingHistory, historyCutoff, STAGE_HISTORY_PRUNE_PER_RUN);
        for (const h of stale) {
          if (h.spId) await this.sp.deleteItem(STAGE_HISTORY_LIST, h.spId);
        }
        if (stale.length > 0) {
          console.warn(`[ProductionGridService] pruned ${stale.length} stage-history row(s) older than ${historyCutoff}; `
            + `${(existingHistory.length - stale.length).toLocaleString()} left.`);
        }
      } catch (e) {
        console.warn("[ProductionGridService] stage-history prune stopped early.", e);
      }

      const historyEntityType = await this.getEntityType(STAGE_HISTORY_LIST);
      const historyLabel = (h: IStageHistoryEntry): string => `history ${h.wipId} → ${h.toStage}`;
      const historyFailed: string[] = [];
      const historyCount = await this.runCreateChunked(
        STAGE_HISTORY_LIST, newHistory, (h) => this.historyBody(h, historyEntityType),
        errors, historyLabel, historyFailed
      );
      // The same "say what SharePoint said" treatment the batch and WIP writes
      // already get. Without it a wholesale refusal reached the screen as
      // "0 stage-history rows synced", which reads as "nothing new" — and that
      // is exactly how these writes failed unnoticed for days.
      if (newHistory.length > 0 && historyCount === 0) {
        errors.push(
          `Every one of the ${newHistory.length} stage-history rows was refused. ` +
          `${STAGE_HISTORY_LIST} holds ${existingHistory.length.toLocaleString()} rows; past ` +
          `SharePoint's 5,000-item view threshold an unindexed column answers with a bare 500. ` +
          `Fix: Grid Settings → Maintenance → "Rebuild index", then re-import.`
        );
      }
      if (onProgress) onProgress("history", historyCount, newHistory.length);


      // 4) Cleanup — remove batch rows no WIP points at anymore. Two sources
      // of these: (a) week-level parent lumps ("WK 0507") created by the
      // earlier import before per-date sub-batch codes existed, and (b) old
      // sub-batches whose WIPs were re-coded by a later import. Both would
      // otherwise sit in the grid forever as misleading zero-WIP rows.
      // In this app batch rows only ever come from imports, so a batch no
      // import references anymore is definitionally dead.
      try {
        const [finalBatches, finalWips] = await Promise.all([this.listBatches(), this.listWipsSlim()]);
        const referenced = new Set(finalWips.map((w) => w.batchId).filter(Boolean));
        const orphans = finalBatches.filter((b) => b.spId && !referenced.has(b.spId));
        for (const orphan of orphans) {
          try { await this.sp.deleteItem(BATCHES_LIST, orphan.spId!); }
          catch (e) { if (errors.length < 50) errors.push(`cleanup ${orphan.batchCode}: ${(e as Error).message}`); }
        }
      } catch (e) {
        if (errors.length < 50) errors.push(`orphan-batch cleanup: ${(e as Error).message}`);
      }

      return { batches: batchCount, wips: wipCount, history: historyCount, errors, failedWipIds };
    });
  }

  // ─── CHUNKED $batch HELPERS ─────────────────────────────────────────────
  /** Creates `rows` in chunks of BATCH_SIZE via $batch, falling back to one-by-one
   * retries for any chunk whose $batch attempt fails. Returns the count created. */
  /**
   * @param label Names the row in any error it produces. Without it a failed
   * write reads "The request is invalid." with nothing tying it to a WIP —
   * which is how a bundle can fail to import, be refused at a scanning
   * station, and leave nobody able to find out which one or why.
   * @param failed Collects the labels that did not make it, so the caller can
   * report them rather than a bare count.
   */
  private async runCreateChunked<T>(
    listName: string,
    rows: T[],
    toBody: (row: T) => Record<string, unknown>,
    errors: string[],
    label?: (row: T) => string,
    failed?: string[],
    /**
     * Called after every chunk with the running total.
     *
     * A write of 16,000 rows used to report ONCE, when it was already over —
     * so a screen could say "Writing…" for minutes and never move. The
     * chunking is already here; only the number was being kept to itself.
     * Optional, so every existing caller is unchanged.
     */
    onChunk?: (done: number) => void,
  ): Promise<number> {
    let count = 0;
    const chunks: T[][] = [];
    for (let i = 0; i < rows.length; i += BATCH_SIZE) chunks.push(rows.slice(i, i + BATCH_SIZE));
    let cursor = 0;
    const worker = async () => {
      while (cursor < chunks.length) {
        const chunk = chunks[cursor++];
        let ok = false;
        try { ok = await this.sendCreateBatch(listName, chunk.map(toBody)); }
        catch (e) {
          // Creates stay all-or-nothing on purpose — see postBatch's
          // changesetPerOp — but the reason should not vanish with the flag.
          console.warn(`[ProductionGridService] $batch create on ${listName} failed — creating its ${chunk.length} rows singly.`, e);
          ok = false;
        }
        if (ok) { count += chunk.length; onChunk?.(count); }
        else {
          for (const row of chunk) {
            try { await this.createWithRetry(listName, toBody(row)); count++; onChunk?.(count); }
            catch (e) {
              const id = label?.(row);
              if (id) failed?.push(id);
              if (errors.length < 50) errors.push(id ? `${id}: ${(e as Error).message}` : (e as Error).message);
            }
          }
        }
        if (cursor < chunks.length) await sleep(CHUNK_PAUSE_MS + Math.random() * CHUNK_PAUSE_MS);
      }
    };
    await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, Math.max(chunks.length, 1)) }, worker));
    return count;
  }

  private async runUpdateChunked<T>(
    listName: string,
    pairs: { id: number; body: T }[],
    toBody: (row: T) => Record<string, unknown>,
    errors: string[],
    label?: (row: T) => string,
    failed?: string[],
    /** Per-chunk running total — see runCreateChunked's own note. */
    onChunk?: (done: number) => void,
  ): Promise<number> {
    let count = 0;
    let systemicHalt = false;
    const chunks: { id: number; body: T }[][] = [];
    for (let i = 0; i < pairs.length; i += BATCH_SIZE) chunks.push(pairs.slice(i, i + BATCH_SIZE));
    let cursor = 0;
    const worker = async () => {
      while (cursor < chunks.length) {
        const chunk = chunks[cursor++];
        /**
         * Which rows to rewrite one at a time — the ones the batch refused,
         * not the whole chunk.
         *
         * The single-item path is ~250ms per row plus a CORS preflight, and
         * retries up to ten times under throttling. Sending a hundred rows
         * down it because one was refused is what saturated the browser's
         * connection pool and left BQC1's own reads queued behind it. A
         * thrown batch is still all of them: nothing came back to say which
         * landed, and an update is idempotent so rewriting is safe.
         */
        let retry: number[];
        try {
          const outcome = await this.sendUpdateBatch(listName, chunk.map((c) => ({ id: c.id, body: toBody(c.body) })));
          if (outcome.systemic !== null) {
            // Nothing in this chunk landed and nothing in the next one would
            // either. Stop the whole update rather than marching through the
            // remaining chunks collecting the same refusal.
            if (!systemicHalt) { systemicHalt = true; errors.push(outcome.systemic); }
            for (const c of chunk) { const n = label?.(c.body); if (n) failed?.push(n); }
            cursor = chunks.length;
            break;
          }
          retry = outcome.retry;
        } catch (e) {
          console.warn(`[ProductionGridService] $batch update on ${listName} failed outright — rewriting its ${chunk.length} rows singly.`, e);
          retry = chunk.map((_, i) => i);
        }
        count += chunk.length - retry.length;
        onChunk?.(count);
        for (const i of retry) {
          const { id, body } = chunk[i];
          try { await this.updateWithRetry(listName, id, toBody(body)); count++; onChunk?.(count); }
          catch (e) {
            const name = label?.(body);
            if (name) failed?.push(name);
            if (errors.length < 50) errors.push(name ? `${name}: ${(e as Error).message}` : (e as Error).message);
          }
        }
        if (cursor < chunks.length) await sleep(CHUNK_PAUSE_MS + Math.random() * CHUNK_PAUSE_MS);
      }
    };
    await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, Math.max(chunks.length, 1)) }, worker));
    return count;
  }

  private itemsUrl(listName: string): string {
    return `${MFG_SITE_URL}/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')/items`;
  }

  private async sendCreateBatch(listName: string, bodies: Record<string, unknown>[]): Promise<boolean> {
    const ops = bodies.map((body) => [
      `POST ${this.itemsUrl(listName)} HTTP/1.1`,
      "Content-Type: application/json;odata=verbose",
      "Accept: application/json;odata=verbose",
      "", JSON.stringify(body),
    ].join("\r\n"));
    const { statuses } = await this.postBatch(ops);
    return statuses.length === bodies.length && statuses.every((s) => s === 200 || s === 201);
  }

  /**
   * Update a chunk, and report WHICH rows failed rather than whether any did.
   *
   * ONE CHANGESET PER ROW, not one changeset for the whole chunk. A changeset
   * is atomic: a single bad row — one throttled write, one row somebody
   * deleted — used to fail all hundred, and the caller then rewrote all
   * hundred one at a time. That is a hundred preflight+PATCH pairs at ~250ms
   * each, retried up to ten times apiece under throttling, for one bad row;
   * it is what put 330 single-item requests and 110 seconds on the wire while
   * BQC1 was trying to load, and it competes for the browser's six connections
   * with every read the screen is waiting on.
   *
   * Per-row changesets keep each write atomic in the only sense that matters
   * here — these rows are independent WIPs, there is no invariant across them
   * — while letting the ninety-nine good ones land.
   *
   * Returns the indexes that did not come back 200/204, so the caller retries
   * exactly those. A response that does not line up with the request (fewer
   * statuses than operations) reports every row as failed: an unreadable
   * answer is not evidence that the writes landed.
   */
  private async sendUpdateBatch(
    listName: string, items: { id: number; body: Record<string, unknown> }[]
  ): Promise<IChunkOutcome> {
    /**
     * MERGE AS THE REQUEST VERB, not POST plus an X-HTTP-Method override.
     *
     * The override header is a workaround for clients that cannot send a verb
     * SharePoint understands. Inside $batch there is no such client: the verb
     * sits on the inner request line, and SharePoint's batch parser reads it
     * from there — the override header is simply not consulted. So every one
     * of these arrived as a literal POST to `…/items(473)`, which SharePoint
     * resolves as an invocation of `GetById(473)`, and it then rejected the
     * body as arguments that method does not take:
     *
     *     The parameter __metadata does not exist in method GetById.
     *
     * All hundred, every time, with a 400 — while the creates in the same run
     * succeeded, because a POST to `…/items` with no id really is a create.
     * That difference is what made this look like a data or schema problem for
     * hours; it was neither.
     */
    const ops = items.map(({ id, body }) => [
      `MERGE ${this.itemsUrl(listName)}(${id}) HTTP/1.1`,
      "Content-Type: application/json;odata=verbose",
      "Accept: application/json;odata=verbose",
      "IF-MATCH: *",
      "", JSON.stringify(body),
    ].join("\r\n"));
    const { statuses, error } = await this.postBatch(ops, { changesetPerOp: true });
    const failed = rowsToRetry(statuses, items.length);
    if (failed.length === 0) return { retry: failed, systemic: null };

    const why = statuses.length === items.length
      ? `statuses ${refusedStatuses(statuses, failed).join(", ")}`
      : `the response carried ${statuses.length} statuses for ${items.length} operations, so none can be assumed written`;
    const said = error === null ? "" : ` SharePoint said: ${error}`;

    const status = systemicRefusal(statuses, failed, items.length);
    if (status !== null) {
      // Every row refused the same way, and rows fail independently here — so
      // the cause is shared, and the single-row path would collect the same
      // refusal N more times while starving the screen's reads of connections.
      // SHAREPOINT'S OWN WORDS FIRST, then our reasoning about them.
      //
      // This read the other way round until an import failed for two hours and
      // could not be diagnosed from the screen: the notice card cut off at its
      // own height, which landed squarely between the explanation and the one
      // sentence that says WHAT IS WRONG. A diagnostic whose answer is below
      // the fold is not a diagnostic. Whatever survives truncation has to be
      // the part somebody can act on.
      const systemic = (error === null
        ? `SharePoint refused all ${items.length} ${listName} updates with ${status} and returned no message. `
        : `SharePoint said: "${error}" — refusing all ${items.length} ${listName} updates with ${status}. `)
        + `The cause is shared (the entity type, a column the list does not have, or the token) rather than `
        + `the rows, so they are not being rewritten singly — that would repeat the same refusal `
        + `${items.length} times and stall every read on the page.`;
      console.error(`[ProductionGridService] ${systemic}`);
      return { retry: [], systemic };
    }

    console.warn(
      `[ProductionGridService] ${failed.length} of ${items.length} ${listName} updates were refused ` +
      `(${why}); rewriting those rows singly.${said}`);
    return { retry: failed, systemic: null };
  }

  /**
   * @param opts.changesetPerOp Wrap each operation in its own changeset, so
   * one refused row cannot roll back the rest. Off by default: creates keep
   * the single atomic changeset, where all-or-nothing is what makes the
   * caller's retry safe — a partially-applied create batch retried whole
   * would DUPLICATE the rows that landed. Updates are idempotent and set it.
   */
  private async postBatch(ops: string[], opts?: { changesetPerOp?: boolean }, maxTries = 10): Promise<IBatchOutcome> {
    const batchId = `batch_${guid()}`;
    const lines: string[] = [];
    if (opts?.changesetPerOp) {
      for (const op of ops) {
        const csId = `changeset_${guid()}`;
        lines.push(`--${batchId}`, `Content-Type: multipart/mixed; boundary="${csId}"`, "");
        lines.push(`--${csId}`, "Content-Type: application/http", "Content-Transfer-Encoding: binary", "Content-ID: 1", "", op);
        lines.push(`--${csId}--`, "");
      }
      lines.push(`--${batchId}--`, "");
    } else {
      const changesetId = `changeset_${guid()}`;
      lines.push(`--${batchId}`, `Content-Type: multipart/mixed; boundary="${changesetId}"`, "");
      let contentId = 1;
      for (const op of ops) lines.push(`--${changesetId}`, "Content-Type: application/http", "Content-Transfer-Encoding: binary", `Content-ID: ${contentId++}`, "", op);
      lines.push(`--${changesetId}--`, `--${batchId}--`, "");
    }
    const payload = lines.join("\r\n");

    let attempt = 0;
    for (;;) {
      const token = await getSharePointToken();
      const res = await fetch(`${MFG_SITE_URL}/_api/$batch`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": `multipart/mixed; boundary="${batchId}"`, Accept: "application/json" },
        body: payload,
      });
      // 429/503 = throttled, 500/504 = server buckling under bulk load — both
      // transient; back off and retry rather than failing the whole chunk.
      if (res.status === 429 || res.status === 503 || res.status === 500 || res.status === 504) {
        attempt++;
        if (attempt >= maxTries) throw new Error(`$batch throttled (${res.status})`);
        const retryAfter = Number(res.headers.get("Retry-After")) || 0;
        await sleep(retryAfter > 0 ? retryAfter * 1000 : Math.min(500 * 2 ** (attempt - 1), 12000) + Math.random() * 400);
        continue;
      }
      const text = await res.text();
      if (!res.ok) throw new Error(`$batch failed: ${res.status} ${res.statusText}\n${text.slice(0, 300)}`);
      return {
        statuses: [...text.matchAll(/HTTP\/1\.1 (\d{3})/g)].map((m) => Number(m[1])),
        error: firstBatchError(text),
      };
    }
  }

  /**
   * Write app-owned sewer (and DS) straight onto the WIP rows, now.
   *
   * The import already reconciles this column from the assignment ledger, but
   * only on the next sync — which may be minutes away, or never on a device
   * where nobody has the grid open. That delay is the whole difference between
   * "assigned" and "assigned as far as anyone else in the building can see", so
   * an Apply writes its own result through immediately and lets the importer
   * keep it right afterwards. Both paths compute the same value from the same
   * ledger, so they cannot disagree; the second one only makes it durable.
   *
   * Deliberately does NOT take the import write lock. The rows are narrow and
   * the value is what a concurrent import would land anyway, and blocking an
   * assignment behind a five-minute bulk import would be a worse trade than any
   * interleaving it avoids.
   *
   * Callers must have established that the app owns the column (valve `live`) —
   * see sewerOwnership.ts. Below that, Excel owns these rows and this must not
   * be called at all.
   */
  public async applyAppSewer(
    rows: readonly { wipId: string; sewer: string; assignedDS?: string }[]
  ): Promise<{ written: number; missing: string[]; errors: string[] }> {
    const missing: string[] = [];
    const errors: string[] = [];
    let written = 0;
    if (rows.length === 0) return { written, missing, errors };

    // Resolve wipId → item Id in chunks. A filter with too many clauses is
    // rejected outright by SharePoint, so this stays well inside the limit
    // rather than reading all 5,000 rows to update a handful.
    const byWip = new Map<string, number>();
    const ids = [...new Set(rows.map((r) => r.wipId).filter(Boolean))];
    for (let i = 0; i < ids.length; i += 20) {
      const chunk = ids.slice(i, i + 20);
      const filter = chunk.map((w) => `wipNumber eq '${w.replace(/'/g, "''")}'`).join(" or ");
      try {
        const found = await this.sp.getListItems<{ Id: number; wipNumber?: string }>(WIPS_LIST, {
          select: "Id,wipNumber", filter, top: 200,
        });
        for (const f of found) if (f.wipNumber) byWip.set(f.wipNumber, f.Id);
      } catch (e) {
        errors.push(`lookup: ${(e as Error).message}`);
      }
    }

    const entityType = await this.getEntityType(WIPS_LIST);
    for (const r of rows) {
      const id = byWip.get(r.wipId);
      if (!id) {
        // Not an error: a WIP the app assigned but the grid has not imported
        // yet. The next sync writes it from the ledger.
        missing.push(r.wipId);
        continue;
      }
      const body: Record<string, unknown> = {
        __metadata: { type: entityType },
        AssignedSewer: r.sewer,
      };
      if (r.assignedDS !== undefined) body.assignedDS = r.assignedDS;
      try {
        await this.updateWithRetry(WIPS_LIST, id, body);
        written++;
      } catch (e) {
        errors.push(`${r.wipId}: ${(e as Error).message}`);
      }
    }
    return { written, missing, errors };
  }

  /**
   * One WIP's set count, corrected by the planner at BQC1.
   *
   * THE ONLY FIELD ON iSyn_WIPs THIS APP WRITES BY HAND. Everything else that
   * touches the list is an import (bulkImport) or the sewer/DS pair the
   * Assignment Board already writes back (applyAppSewer) — which is the
   * precedent this follows, down to the targeted single-item PATCH rather than
   * anything resembling a bulk write.
   *
   * NOT a backfill, and backfillGuard says so in its own header: this is the
   * app witnessing a person deliberately changing one record, the same class
   * of write as BQC2 recording a scan an operator just took. The guard exists
   * for BULK writes of rows the app never saw.
   *
   * IT DOES NOT SURVIVE A RE-IMPORT OF ITS OWN J.O. SHEET. `qty` is a
   * PLAN-owned column (see joPlanImport's PLAN field list), so importing that
   * row's sheet again puts the sheet's figure back. That is correct — the
   * sheet is the source of the plan — but it has to be said on screen, and
   * BQC1's board says it.
   */
  public async setWipQty(spId: number, qty: number): Promise<void> {
    if (!Number.isInteger(qty) || qty < 1) {
      throw new Error(`Refusing to write a set count of ${qty} — it must be a whole number of at least 1.`);
    }
    const entityType = await this.getEntityType(WIPS_LIST);
    await this.updateWithRetry(WIPS_LIST, spId, { __metadata: { type: entityType }, qty });
  }

  private async createWithRetry(listName: string, body: Record<string, unknown>, maxTries = 10): Promise<void> {
    let attempt = 0;
    for (;;) {
      try {
        await this.sp.createItem(listName, body);
        return;
      } catch (err) {
        attempt++;
        if (!isRetryableStatus((err as Error).message ?? "") || attempt >= maxTries) throw err;
        await sleep(Math.min(500 * 2 ** (attempt - 1), 12000) + Math.random() * 400);
      }
    }
  }

  private async updateWithRetry(listName: string, id: number, body: Record<string, unknown>, maxTries = 10): Promise<void> {
    let attempt = 0;
    for (;;) {
      try {
        await this.sp.updateItem(listName, id, body);
        return;
      } catch (err) {
        attempt++;
        if (!isRetryableStatus((err as Error).message ?? "") || attempt >= maxTries) throw err;
        await sleep(Math.min(500 * 2 ** (attempt - 1), 12000) + Math.random() * 400);
      }
    }
  }

  // ─── MAPPING (read) ──────────────────────────────────────────────────
  private mapWip(r: ISpWipRow, batchCodes?: ReadonlyMap<number, string>): IProductionWip {
    return {
      spId: r.Id,
      wipId: r.wipNumber ?? String(r.Id),
      orderId: r.orderId ?? undefined,
      serialId: r.serialId ?? undefined,
      cutter: r.cutter ?? undefined,
      design: r.design ?? undefined,
      sku: r.sku ?? "",
      category: (r.category as Category) ?? undefined,
      qty: r.qty ?? 0,
      currentStage: (r.currentStage as CurrentStage) ?? undefined,
      dateCreated: r.dateCreated ?? undefined,
      dueDate: r.dueDate ?? undefined,
      storeChannel: (r.storeChannel as StoreChannel) ?? undefined,
      notes: r.notes ?? undefined,
      batchId: r.BatchIDId ?? undefined,
      // The expanded title where the read asked for it, the header lookup
      // where it did not — see WIP_BATCH_EXPANSION for why most reads do not.
      batchCode: r.BatchID?.Title
        ?? (r.BatchIDId != null ? batchCodes?.get(r.BatchIDId) : undefined)
        ?? undefined,
      assignedSewer: r.AssignedSewer ?? undefined,
      color: r.color ?? undefined,
      size: r.size ?? undefined,
      assignedDS: r.assignedDS ?? undefined,
      deliveryStatus: r.deliveryStatus ?? undefined,
      receivedQty: r.receivedQty ?? undefined,
      dispatchedAt: r.dispatchedAt ?? undefined,
      receivedAt: r.receivedAt ?? undefined,
      cutOperator: r.cutOperator ?? undefined,
      modified: r.Modified ?? undefined,
      joSeries: r.joSeries ?? undefined,
      wkSeries: r.wkSeries ?? undefined,
      dateAssignedToCutter: r.dateAssignedToCutter ?? undefined,
      inboundedToRtgStockroom: r.inboundedToRtgStockroom ?? undefined,
      packed: r.packed ?? undefined,
      cancellationStatus: (r.cancellationStatus as WipCancellationStatus) ?? undefined,
      directMatl: r.directMatl ?? undefined,
      cogm: r.cogm ?? undefined,
      grn: r.grn ?? undefined,
      qcPassedQty: r.qcPassedQty ?? undefined,
      qcPassedAt: r.qcPassedAt ?? undefined,
      cutAt: r.cutAt ?? undefined,
      qcPersonnel: r.qcPersonnel ?? undefined,
      qcRepairQty: r.qcRepairQty ?? undefined,
    };
  }

  private mapBatch(r: ISpBatchRow): IBatchHeader {
    return {
      spId: r.Id,
      batchCode: r.Title ?? r.wkSeries ?? "",
      dateOrdered: r.dateOrdered ?? undefined,
      prodPlan: r.prodPlan ?? 0,
      cutTotal: r.cutTotal ?? undefined,
      assignedTotal: r.assignedTotal ?? undefined,
      receivedTotal: r.receivedTotal ?? undefined,
      qcTotal: r.qcTotal ?? undefined,
      batchStatus: (r.batchStatus as BatchStatus) ?? "Active",
      notes: r.notes ?? undefined,
    };
  }

  // ─── MAPPING (write) ─────────────────────────────────────────────────
  private wipBody(
    w: IProductionWip, entityType: string, preserveSewer = false,
    omitFields?: ReadonlySet<string>
  ): Record<string, unknown> {
    // `preserveSewer` also holds the DS number, because the app writes the two
    // TOGETHER. publishAssignedSewer stamps AssignedSewer and assignedDS in one
    // call on every dispatch, so protecting one and not the other leaves a WIP
    // carrying the app's sewer and the workbook's (blank) DS — a row that says
    // ARINGO has it and nothing says on which slip. Whoever is holding the
    // printed paper is then the only place that pairing exists.
    //
    // This bites exactly in the window the app is meant to cover: dispatch
    // recorded here, ASSIGNING export not run yet, an import lands in between.
    //
    // dispatchedAt is deliberately NOT in this set. It is defined across the
    // codebase as strictly the workbook's own BQC3 row stamp, and
    // liveDispatchTopUp reads it as its cutover signal — a WIP dispatched in
    // the app is topped up from the live slips until the workbook confirms it,
    // then drops out. Freeze that field and those WIPs never drop out, and the
    // double-count the module exists to prevent happens instead.
    const body: Record<string, unknown> = {
      __metadata: { type: entityType },
      Title: w.wipId,
      wipNumber: w.wipId,
      orderId: w.orderId ?? null,
      serialId: w.serialId ?? null,
      cutter: w.cutter ?? null,
      design: w.design ?? null,
      sku: w.sku,
      category: w.category ?? null,
      qty: w.qty,
      currentStage: w.currentStage ?? null,
      dateCreated: w.dateCreated ? `${w.dateCreated}T00:00:00Z` : null,
      dueDate: w.dueDate ? `${w.dueDate}T00:00:00Z` : null,
      storeChannel: w.storeChannel ?? null,
      notes: w.notes ?? null,
      AssignedSewer: w.assignedSewer ?? null,
      color: w.color ?? null,
      size: w.size ?? null,
      assignedDS: w.assignedDS ?? null,
      deliveryStatus: w.deliveryStatus ?? null,
      receivedQty: w.receivedQty ?? null,
      dispatchedAt: w.dispatchedAt ? `${w.dispatchedAt}T00:00:00Z` : null,
      receivedAt: w.receivedAt ? `${w.receivedAt}T00:00:00Z` : null,
      cutOperator: w.cutOperator ?? null,
      joSeries: w.joSeries ?? null,
      wkSeries: w.wkSeries ?? null,
      dateAssignedToCutter: w.dateAssignedToCutter ? `${w.dateAssignedToCutter}T00:00:00Z` : null,
      inboundedToRtgStockroom: w.inboundedToRtgStockroom ?? null,
      packed: w.packed ?? null,
      cancellationStatus: w.cancellationStatus ?? null,
      directMatl: w.directMatl ?? null,
      cogm: w.cogm ?? null,
      grn: w.grn ?? null,
      qcPassedQty: w.qcPassedQty ?? null,
      qcPassedAt: w.qcPassedAt ? `${w.qcPassedAt}T00:00:00Z` : null,
      cutAt: w.cutAt ? `${w.cutAt}T00:00:00Z` : null,
      qcPersonnel: w.qcPersonnel ?? null,
      qcRepairQty: w.qcRepairQty ?? null,
    };
    if (w.batchId) body.BatchIDId = w.batchId;
    // A field absent from a MERGE body is left exactly as SharePoint holds it.
    // Deleting the key is therefore the difference between "no opinion" and
    // `AssignedSewer: null`, which would clear the column outright.
    if (preserveSewer) {
      delete body.AssignedSewer;
      delete body.assignedDS;
    }
    // A list this site hasn't run "Add missing columns" on rejects the whole
    // write the moment the body names one of these — see bulkImport()'s
    // up-front missingFields() check, which is what actually populates this.
    if (omitFields) for (const f of omitFields) delete body[f];
    return body;
  }

  /**
   * RETAG EVERY WIP'S CATEGORY FROM THE PRODUCT CATALOG.
   *
   * WHY THIS EXISTS. The BQC1 plan import writes `category`, and until
   * 2026-09-14 it wrote it WITHOUT consulting the catalog — so a JO-only file,
   * which carries no CATEGORY column and no DATABASE sheet, fell through to
   * the parser's last resort and stamped CLASSIC on every WIP it touched. The
   * import is fixed, but a fixed import only corrects the rows a later run
   * happens to cover, and the wrong values are in the list NOW. Category is
   * what every COGM rate is looked up by, so leaving them is leaving a price
   * wrong.
   *
   * IT WRITES ONE COLUMN, and only where the catalog actually disagrees. Not a
   * re-import, not a backfill: nothing is copied in from anywhere, no record is
   * created, and the four station columns this class now withholds from a full
   * import are not in the body either. It corrects a derived field of the
   * Grid's own table from a table compiled into the app.
   *
   * A CATALOG VALUE OUTSIDE THE APP'S VOCABULARY IS NOT WRITTEN. LAB GOWN is
   * real in the catalog and is not a value the SharePoint Choice column
   * accepts, so those rows are counted and left alone rather than refused one
   * by one at write time. Same rule masterCategory.ts applies at import.
   */
  public async previewCategoryRetag(): Promise<ICategoryRetagPlan> {
    const wips = await this.listWipsSlim();
    const fix: ICategoryRetagRow[] = [];
    let alreadyRight = 0;
    let unknownDesign = 0;
    let notAnAppCategory = 0;

    for (const w of wips) {
      const parent = parentSkuOf(undefined, w.sku);
      if (!parent) { unknownDesign++; continue; }
      const hit = (categoryOfParent(parent) ?? "").trim().toUpperCase();
      if (!hit) { unknownDesign++; continue; }
      if (!CATEGORY_SET.has(hit)) { notAnAppCategory++; continue; }
      if ((w.category ?? "") === hit) { alreadyRight++; continue; }
      fix.push({ spId: w.spId, wipId: w.wipId, from: w.category, to: hit as Category });
    }
    return { fix, alreadyRight, unknownDesign, notAnAppCategory, scanned: wips.length };
  }

  /** Write the plan. `category` and nothing else. */
  public async runCategoryRetag(
    plan: ICategoryRetagPlan,
    onProgress?: (done: number, total: number) => void,
  ): Promise<{ updated: number; errors: string[] }> {
    return this.withWriteLock("Retag categories", async () => {
      const errors: string[] = [];
      if (plan.fix.length === 0) return { updated: 0, errors };
      const type = await this.getEntityType(WIPS_LIST);
      const total = plan.fix.length;
      const updated = await this.runUpdateChunked(
        WIPS_LIST,
        plan.fix.map((r) => ({ id: r.spId, body: r })),
        (r) => ({ __metadata: { type }, category: r.to }),
        errors,
        (r) => `WIP ${r.wipId}`,
        undefined,
        (done) => onProgress?.(done, total),
      );
      return { updated, errors };
    });
  }

  private batchBody(
    b: IBatchHeader, entityType: string, omitFields?: ReadonlySet<string>
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {
      __metadata: { type: entityType },
      Title: b.batchCode,
      wkSeries: b.batchCode,
      dateOrdered: b.dateOrdered ? `${b.dateOrdered}T00:00:00Z` : null,
      prodPlan: b.prodPlan,
      cutTotal: b.cutTotal ?? null,
      assignedTotal: b.assignedTotal ?? null,
      receivedTotal: b.receivedTotal ?? null,
      qcTotal: b.qcTotal ?? null,
      batchStatus: b.batchStatus,
      notes: b.notes ?? null,
    };
    // Same treatment the WIP body gets: a field the list does not have is
    // omitted rather than sent, so the rest of the row still lands instead of
    // SharePoint refusing all hundred of them.
    if (omitFields) for (const f of omitFields) delete body[f];
    return body;
  }

  private historyBody(h: IStageHistoryEntry, entityType: string): Record<string, unknown> {
    return {
      __metadata: { type: entityType },
      Title: `${h.wipId}-${h.toStage}`,
      wipId: h.wipId,
      fromStage: h.fromStage ?? null,
      toStage: h.toStage,
      transitionAt: `${h.transitionAt}T00:00:00Z`,
      operator: h.operator || "Import",
    };
  }

  /** True if any field on an existing SharePoint WIP differs from the incoming one. */
  private wipChanged(
    existing: IProductionWip,
    incoming: IProductionWip,
    preserveSewer = false,
    /**
     * The columns this run's write will DROP, because the list does not have
     * them yet — the same set wipBody deletes from its body.
     *
     * The same discipline as preserveSewer below, generalised: a field the
     * write omits must not be able to mark a row as changed. Without this,
     * every import between shipping a new column and running "Add missing
     * columns" reads thousands of rows as changed on a value it is not going
     * to send, and queues thousands of updates that do nothing — straight
     * into SharePoint's throttle, every run, until somebody runs the valve.
     *
     * Compared against the IProductionWip key directly. That holds for the
     * schema gap because the only fields that can be missing are
     * WIP_MOTHER_FIELDS, whose SharePoint names and interface keys are the
     * same word. A plan run widens this set past that (see joPlanImport.ts),
     * and covers the difference by carrying BOTH spellings of the two
     * renamed columns it protects — `AssignedSewer` for wipBody's body key
     * and `assignedSewer` for the key compared here. A spelling that matches
     * neither consumer is simply inert, which is why one set can serve both.
     */
    omitFields?: ReadonlySet<string>
  ): boolean {
    const omitted = (f: keyof IProductionWip): boolean => !!omitFields?.has(f as string);
    const textFields: (keyof IProductionWip)[] = [
      "orderId", "serialId", "cutter", "design", "sku", "category", "currentStage",
      "storeChannel", "color", "size", "deliveryStatus", "cutOperator", "notes",
      "joSeries", "wkSeries", "cancellationStatus", "grn", "qcPersonnel",
      // Omitted from the comparison when the body will omit the field too:
      // otherwise every WIP whose workbook sewer differs from the stored one is
      // "changed" on every sync forever, and each of those updates writes a body
      // that deliberately does nothing about it — thousands of pointless writes
      // straight into SharePoint's throttle.
      // Kept in step with wipBody above: a field the write omits must not be
      // able to mark a row as changed, or every import queues an update whose
      // only difference is a value it is not going to send.
      ...(preserveSewer ? [] : (["assignedSewer", "assignedDS"] as (keyof IProductionWip)[])),
    ];
    for (const f of textFields) {
      if (omitted(f)) continue;
      if (((existing[f] as string) ?? "").trim() !== ((incoming[f] as string) ?? "").trim()) return true;
    }
    const numFields: (keyof IProductionWip)[] = ["qty", "receivedQty", "directMatl", "cogm", "qcPassedQty", "qcRepairQty"];
    for (const f of numFields) {
      if (omitted(f)) continue;
      if (Number(existing[f] ?? 0) !== Number(incoming[f] ?? 0)) return true;
    }
    const dateFields: (keyof IProductionWip)[] = ["dueDate", "dateCreated", "dispatchedAt", "receivedAt", "dateAssignedToCutter", "qcPassedAt", "cutAt"];
    for (const f of dateFields) {
      if (omitted(f)) continue;
      const a = ((existing[f] as string) ?? "").slice(0, 10);
      const b = ((incoming[f] as string) ?? "").slice(0, 10);
      if (a !== b) return true;
    }
    const boolFields: (keyof IProductionWip)[] = ["inboundedToRtgStockroom", "packed"];
    for (const f of boolFields) {
      if (omitted(f)) continue;
      if (!!existing[f] !== !!incoming[f]) return true;
    }
    if ((existing.batchId ?? null) !== (incoming.batchId ?? null)) return true;
    return false;
  }

  /**
   * Every iSyn_WIPs row, reduced to what a de-duplication plan can judge.
   *
   * READS THE WHOLE LIST, deliberately unfiltered. The plan has to see every
   * row carrying a given WIP number or it cannot tell which is the fullest —
   * and a filtered read that missed one would nominate a keeper on partial
   * evidence and delete the real record. Paging handles the size; the
   * threshold does not apply to an unsorted, unfiltered enumeration.
   *
   * The expanded BatchID object is reduced to its plain id: its key order is
   * not guaranteed, and two rows agreeing on the batch must never read as a
   * conflict because their lookup objects serialised differently.
   */
  public async listWipRowsForDedupe(): Promise<IDedupeRow[]> {
    // No expand: this read discards the expanded object two lines below, so
    // it was paying a full-list lookup crawl for a value it deletes.
    const rows = await this.readWipRows({ top: 5000 });
    return rows.map(r => ({
      id: r.Id,
      wipId: (r.wipNumber ?? "").trim(),
      // Which columns are excluded, and why, lives in wipDedupe's
      // NOT_COMPARABLE — a destructuring pattern here is exactly where the
      // second spelling of the item id got missed.
      fields: comparableFields(r as unknown as Record<string, unknown>),
    }));
  }

  /**
   * Deletes iSyn_WIPs rows by item id.
   *
   * ONE AT A TIME, sequentially, reporting after each. A parallel burst
   * against thousands of ids invites throttling, and a throttled delete that
   * half-succeeds leaves nobody able to say which half — this is not
   * re-runnable in the way a write is, so knowing exactly where it stopped
   * matters more than finishing quickly.
   *
   * A failure does not abort the run: one row refusing to delete is no reason
   * to strand the rest, and every id that failed comes back named.
   */
  public async deleteWipRows(
    ids: readonly number[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<{ deleted: number[]; failed: { id: number; error: string }[] }> {
    const deleted: number[] = [];
    const failed: { id: number; error: string }[] = [];
    for (const id of ids) {
      try {
        await this.sp.deleteItem(WIPS_LIST, id);
        deleted.push(id);
      } catch (e) {
        failed.push({ id, error: e instanceof Error ? e.message : String(e) });
      }
      onProgress?.(deleted.length + failed.length, ids.length);
    }
    return { deleted, failed };
  }

  private async getEntityType(listName: string): Promise<string> {
    const cached = this.entityTypeCache.get(listName);
    if (cached) return cached;
    const data = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')?$select=ListItemEntityTypeFullName`
    );
    this.entityTypeCache.set(listName, data.d.ListItemEntityTypeFullName);
    return data.d.ListItemEntityTypeFullName;
  }
}

export const productionGridService = new ProductionGridService();
